import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { fmtCurrency, fmtQty, parseMoney, sumMoney, fmtPayType, fmtPayPercentage, isPercentagePayType, PAY_TYPE_LABELS, ASSIGNMENT_PAY_TYPES } from '../lib/display-labels';
import { TechSlot, techSlotsOf } from '../lib/tech-slots';
import { crewNamedOnLine, flatRateEarnings, FlatRateEarnings } from '../lib/flat-rate-pay';
import {
  overrideStateOf, overriddenCount, overrideAuthorName, formatOverrideAt, OverrideState,
} from '../lib/pay-override';
import { getLineItemTypeLabel } from '../constants/line-item-types';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { useUnapprovedPayLeaveNotice } from '../hooks/useUnapprovedPayLeaveNotice';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { formatCalendarDate } from '../lib/calendar-date';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../lib/load-failure';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import {
  ShiftsRule, ShiftsResolution, shiftsRuleOf, shiftsResolutionOf, takesShifts, parseShifts, shiftsTally, fmtShifts,
} from '../lib/day-rate-shifts';
import {
  Sparkles,
  Loader,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Lock,
  Trash2,
  Plus,
  CheckCircle,
  Save,
  Users,
  FileText,
  Calendar,
  Clock,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';

// --- Types ---
type ConfidenceLevel = 'high' | 'medium' | 'low';

interface PayAssignment {
  id?: string;
  ticket_line_item_id: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
  initials?: string;
  pay_type: string;
  percentage_applied: number | null;
  rate_applied: number | null;
  base_amount: number | null;
  calculated_amount: number | null;
  role_on_ticket: string | null;
  approved: boolean;
  // ── The override columns (API #537 / migration 0371) ─────────────────────
  // GET selects `tpa.*`, so these arrive snake_case and untransformed, and
  // NUMERIC comes over as a string. See src/lib/pay-override.ts, which is the
  // only place they are interpreted.
  //
  // `computed_amount` absent means the server could not price the row. That is
  // a legitimate state and it is what all 273 existing assignments read today
  // (0371 deliberately did not backfill) — so it must render as "not computed"
  // and never as $0.00.
  computed_amount?: number | string | null;
  amount_overridden?: boolean | null;
  amount_overridden_by?: string | null;
  amount_overridden_at?: string | null;
  line_item_type?: string;
  line_item_description?: string;
  line_item_total?: number | null;
  line_item_rate?: number;
  line_item_quantity?: number;
  // ── Day rate × shifts (api#1181 + its read follow-up) ────────────────────
  // `shifts_worked` is the office's own figure for this hand on this line —
  // NUMERIC, so a string ("2.00") or null. `shifts_rule` / `shifts_resolution`
  // are the API's answer to "does this line take shifts, and how many is this
  // hand paid for"; see src/lib/day-rate-shifts.ts. Absent means the payload
  // predates them, NOT that the line pays once.
  shifts_worked?: number | string | null;
  shifts_rule?: string | null;
  shifts_resolution?: unknown;
  /**
   * Set when the office has edited shifts on this row's LINE since the server
   * last answered. The resolution then describes a split that is no longer on
   * screen, so it is not shown and the amount reads as absent until Save.
   * Display-only — never sent.
   */
  shifts_stale?: boolean;
  /**
   * Set when AI Suggest reported this hand in `needsRate[]` (api#1184): a
   * EQUIPMENT line (per-day in api#1184, any unit from api#1185) pays a percentage, and this hand has none for the
   * line's type. The row has no amount and must not be approved until a
   * percentage is set or the office overrides the amount.
   * Display-only — never sent.
   */
  needs_rate?: boolean;
  // Populated only for rows that came from an AI suggestion in this session.
  // Display-only — never sent back to the API.
  ai_confidence?: ConfidenceLevel;
  ai_reasoning?: string;
}

/**
 * One finding from POST /tickets/:id/pay-assignments/tier-check.
 *
 * IT REPORTS WHAT WAS NOTICED AND NOT WHAT IS CORRECT, and that is deliberate
 * on the API side too — there is no `suggestedPercentage` and no amount to
 * render. `ticket_pay_assignments` records nowhere that a percentage came from,
 * so a stale tier and a percentage the office typed are the same row, and the
 * heuristic that would separate them misfires exactly where it costs money:
 * Derek's dual rate IS 0.1700 and 17% is what an office types. Naming a figure
 * here would turn an observation into an instruction.
 *
 * `seed` is the one exception and is not a recalculation of anybody's decision:
 * it is the default for a row that has NO percentage yet, and only ever the
 * worker's own tier for the current crew.
 */
type TierStatus =
  | 'ok' | 'tier_drift' | 'crew_ambiguous' | 'no_rate'
  | 'not_a_tier_rate' | 'approved' | 'unseeded';

interface TierFinding {
  index: number;
  status: TierStatus;
  crewSize: number;
  requiredTier: 'single' | 'dual' | 'triple' | 'quad' | null;
  appliedTier: 'single' | 'dual' | 'triple' | 'quad' | null;
  seed: { payType: string; percentageApplied: number } | null;
}

/**
 * One row the server priced differently from the figure the screen sent, as
 * `costing.differences` reports it.
 *
 * This is the response half that used to be discarded, and discarding it is
 * what let the office type a number, watch it be accepted and be paid another.
 * It describes ONE WRITE — it is not a column and it cannot be re-read — so it
 * lives for as long as the screen is open and no longer.
 */
interface ServerDifference {
  assignmentId: string;
  client: { payType: string | null; calculatedAmount: number | null };
  server: { payType: string; calculatedAmount: number; crewSize: number | null };
}

/**
 * One row the server REFUSED to cost, as the 400 body's `refusals[]` reports it.
 *
 * This is the other half of the response that used to be discarded, and it is
 * the half that costs a payroll run: on a refusal the API writes NOTHING —
 * `tickets.routes.ts` returns before the insert — so the office sees one long
 * sentence naming every bad row, watches it disappear after five seconds, and
 * has no way to tell which of fifteen rows it was talking about.
 *
 * The rows arrive with `ticketLineItemId` and `userId`, which is the identity a
 * row on this screen has BEFORE it is saved. Keying on `assignmentId` — what
 * the success path uses — cannot work here: a refused row was never inserted
 * and has no id.
 *
 * `invalid_override` is the exception and carries neither id; it is matched by
 * `index` into the request body instead. See the resolution in `onError`.
 */
interface SaveRefusal {
  ticketLineItemId?: string;
  userId?: string;
  reason: string;
  message: string;
}

/**
 * WHAT A REFUSAL CAN BE FIXED BY — and it is deliberately not one answer for
 * all of them, because the wrong answer here teaches an expensive habit.
 *
 * `crew_ambiguous` is the row the costing genuinely cannot price: the line's
 * tech slots do not say how many hands worked it, so the percentage pool has no
 * divisor and the person-day quantity has no crew. Nothing on a rate card fixes
 * that. The override hatch exists for exactly this row, and the API agrees — an
 * overridden row rides past the batch refusal.
 *
 * `no_percentage_rate` is the opposite and must NEVER be pointed at the
 * override. The worker holds a flat rate and no percentage tier; the row is on
 * the wrong BASIS, and the fix is the Day Rate switch already on the row. An
 * override there types a number over a question the system could have answered,
 * and afterwards nobody downstream can tell that figure from a computed one.
 *
 * A reason not in this map gets NO remedy sentence — the server's own message
 * still renders on the row. Silence is the safe default here: the failure this
 * map exists to prevent is offering the override to a row that should not have
 * it, so an unrecognised reason must not fall through to one.
 */
type RefusalRemedy = 'override' | 'pay_type' | 'rate_card' | 'ticket' | 'shifts' | 'percentage';

const REFUSAL_REMEDY: Record<string, RefusalRemedy> = {
  crew_ambiguous: 'override',
  no_percentage_rate: 'pay_type',
  unsupported_pay_type: 'pay_type',
  no_rate: 'rate_card',
  tier_missing: 'rate_card',
  no_day_rate: 'rate_card',
  no_hourly_rate: 'rate_card',
  no_line_total: 'ticket',
  no_hours: 'ticket',
  // A per-day labour line the server cannot split by itself, or entered
  // shifts that do not add up. Fixed on THIS screen, in the shifts boxes —
  // not by an override, which would hide the split instead of recording it.
  needs_shifts: 'shifts',
  shifts_mismatch: 'shifts',
  // An EQUIPMENT line (per-day in api#1184, any unit from api#1185) pays a percentage, and this hand has none for its
  // type (api#1184). Fixed on the rate card, or by an explicit override — the
  // save door lets an overridden row past, and says so.
  needs_rate: 'percentage',
};

/** What the worker's pay-rate row says about the basis a row was switched to. */
type ConfiguredRate = { status: 'found'; amount: number } | { status: 'none' | 'loading' | 'error' };

/** How a tier is said out loud. `quad` is the 4+ tier, not the four tier. */
const TIER_HANDS: Record<string, string> = {
  single: '1 hand',
  dual: '2 hands',
  triple: '3 hands',
  quad: '4 or more hands',
};

interface AiSuggestion {
  ticketLineItemId: string;
  lineItemType: string;
  lineItemDescription: string | null;
  lineItemTotal: number | null;
  userId: string;
  employeeName: string;
  employeeInitials: string | null;
  payType: string;
  percentageApplied: number | null;
  rateApplied: number | null;
  calculatedAmount: number | null;
  roleOnTicket: string;
  confidence: ConfidenceLevel;
  reasoning: string;
}

/** A hand pay-ai made NO suggestion for, because the line needs shifts entered (api#1181). */
interface AiNeedsShifts {
  ticketLineItemId: string;
  lineItemDescription: string | null;
  userId: string;
  employeeName: string;
  reason: 'needs_shifts' | 'shifts_mismatch';
  message: string;
}

/** A hand pay-ai made NO suggestion for: an equipment line, and no percentage for its type (api#1184). */
interface AiNeedsRate {
  ticketLineItemId: string;
  lineItemDescription: string | null;
  userId: string;
  employeeName: string;
  reason: 'needs_rate';
  message: string;
}

interface AiResult {
  ticketId: string;
  ticketNumber: string;
  suggestions: AiSuggestion[];
  needsShifts?: AiNeedsShifts[];
  needsRate?: AiNeedsRate[];
  overallConfidence: ConfidenceLevel;
  reasoning: string;
}

/**
 * A row of `ticket_line_items` exactly as GET /tickets/:id hands it back.
 *
 * That endpoint does `SELECT * FROM ticket_line_items ... ORDER BY display_order`
 * and spreads the rows onto the ticket untransformed, so the keys are snake_case
 * and the numerics arrive as strings. The camelCase alternatives are the
 * read-tolerance rule in CLAUDE.md, not a second shape the API is known to send.
 */
interface TicketLineItem {
  id?: string;
  line_item_type?: string;
  lineItemType?: string;
  description?: string | null;
  rate?: number | string | null;
  quantity?: number | string | null;
  total?: number | string | null;
  // The assigned_tech_1..4_id / _initials pairs are read through techSlotsOf,
  // which indexes them by name — declaring them individually here would be four
  // more places to keep in step with the API for no gain.
  [key: string]: unknown;
}

interface LineItemGroup {
  lineItemId: string;
  type: string;
  description: string;
  /**
   * The line's revenue, or null when the ticket does not carry one. Absent is
   * not zero: a line whose total the server never computed is not a line worth
   * $0.00, and it seeds base_amount on a manually added row — a zero there is a
   * percentage of nothing, silently.
   */
  total: number | null;
  rate: number | null;
  quantity: number | null;
  assignments: PayAssignment[];
  /** Does this line take shifts? null when neither payload says. */
  shiftsRule: ShiftsRule | null;
  /**
   * Who the FIELD named on this line — not who has been assigned pay.
   * Empty for a group rebuilt from an assignment, because that fallback shape
   * carries the joined tli.* pay columns and not the tech slots.
   */
  techSlots: TechSlot[];
}

// --- Helpers ---
/**
 * How a row is identified BEFORE it has an id.
 *
 * A refused row was never inserted, so `assignment.id` is undefined and the
 * pair the server named is all there is. Both halves are required at the call
 * site — an empty half would collide every unsaved row onto one key and mark
 * the wrong hand's pay.
 */
function refusalKey(ticketLineItemId: string, userId: string) {
  return `${ticketLineItemId}::${userId}`;
}

function getInitials(first?: string, last?: string) {
  return `${(first || '?')[0]}${(last || '?')[0]}`.toUpperCase();
}

/**
 * What a day_rate row is worth, priced from its LINE and not from its rate
 * alone.
 *
 * `subsistence` bills person-days, so the hand is owed rate × the line's
 * quantity ÷ the hands NAMED on the line — the same arithmetic payroll runs
 * when it pays the row, so what the office approves here is what the hand is
 * then paid. Every other flat type is the rate itself, once. See
 * src/lib/flat-rate-pay.ts for both halves and the gate between them.
 *
 * ONE computation for the recompute and the row's display, deliberately: a
 * breakdown drawn from a second copy of the maths would be a figure the
 * amount above it can disagree with.
 *
 * The group is the ticket's own line (type, quantity, tech slots). A row whose
 * line is not in the ticket payload falls back to its joined tli.* columns for
 * type and quantity — and to NO tech slots, because the join does not carry
 * them, so a person-day row there is refused rather than guessed.
 */
function priceDayRate(group: LineItemGroup | undefined, a: PayAssignment): FlatRateEarnings | null {
  if (a.pay_type !== 'day_rate' || a.rate_applied == null) return null;
  return flatRateEarnings({
    lineItemType: group?.type ?? a.line_item_type,
    quantity: group?.quantity ?? a.line_item_quantity,
    crewNamed: crewNamedOnLine(group?.techSlots ?? []),
    dayRateAmount: Number(a.rate_applied),
  });
}

/**
 * HOW SURE THE AI WAS, as the saturated badge idiom — a `--fi-signal-*` fill
 * with `text-fi-signal-ink` on it.
 *
 * THIS REPLACES A BADGE THAT WAS UNREADABLE IN LIGHT MODE, and the mechanism
 * is worth naming because it is not visible in the class list. The three
 * levels were a dark-navy wash (`bg-{hue}-900/30`) carrying 400-weight ink.
 * theme.css remaps every `text-{hue}` utility to an AA-safe DARK value under
 * the light attribute — green to #15803d, amber to #b45309, red to #dc2626 —
 * but it remaps no `-900/30` FILL, because only the `/20` opacities are in
 * that block. So in light mode the fill stayed dark navy-green and the ink
 * became dark green on top of it. The remap that exists to rescue the ink is
 * what put it there.
 *
 * The signal hues do not follow the theme, and `--fi-signal-ink` is measured
 * against all five of them, so one dark ink holds in both. Nothing here needs
 * a light twin and nothing here can be remapped out from under itself.
 *
 * `tint` is the separate, low-opacity wash for the ROW — a fill is for a
 * badge, a tint is for a surface, and putting the badge's fill behind a whole
 * row would make every medium-confidence row shout.
 */
const confidenceBadge = (level: ConfidenceLevel) => {
  if (level === 'high') {
    return {
      bg: 'bg-fi-signal-green', text: 'text-fi-signal-ink', border: 'border-black/20',
      ink: 'text-fieldiq-text', Icon: CheckCircle, label: 'High confidence',
    };
  }
  if (level === 'medium') {
    return {
      bg: 'bg-fi-signal-amber', text: 'text-fi-signal-ink', border: 'border-black/20',
      ink: 'text-fieldiq-text', Icon: AlertCircle, label: 'Medium confidence',
    };
  }
  return {
    bg: 'bg-fi-signal-red', text: 'text-fi-signal-ink', border: 'border-black/20',
    ink: 'text-fieldiq-text', Icon: AlertTriangle, label: 'Low confidence',
  };
};

/**
 * Row emphasis for anything the AI wasn't sure about — these feed payroll
 * unverified.
 *
 * The RAIL carries the hue and the wash is neutral. A coloured wash under a
 * whole row is the thing that made this screen shout in light mode, and the
 * rail is already the app's idiom for a status on a register row.
 */
const CONFIDENCE_ROW_STYLE: Record<ConfidenceLevel, string> = {
  high: '',
  medium: 'border-l-2 border-l-fi-signal-amber bg-fieldiq-dark/30',
  low: 'border-l-2 border-l-fi-signal-red bg-fieldiq-dark/40',
};

const CONFIDENCE_RANK: Record<ConfidenceLevel, number> = { low: 0, medium: 1, high: 2 };

function lowestConfidence(levels: (ConfidenceLevel | undefined)[], fallback: ConfidenceLevel): ConfidenceLevel {
  const present = levels.filter(Boolean) as ConfidenceLevel[];
  if (present.length === 0) return fallback;
  return present.reduce((lowest, l) => (CONFIDENCE_RANK[l] < CONFIDENCE_RANK[lowest] ? l : lowest), present[0]);
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function PayAssignmentScreen() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * `sticky` survives the 5-second auto-dismiss.
   *
   * A flash that says the server stored figures other than the ones on screen
   * is not a flash — it is the finding, and a finding that erases itself after
   * five seconds is the same silence this screen is being fixed for. Found by
   * looking at the rendered page rather than at the test: the per-row markers
   * were still there and the banner had already gone.
   *
   * Sticky messages carry a dismiss control, because nothing else takes them
   * down.
   */
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string; sticky?: boolean } | null>(null);
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [localAssignments, setLocalAssignments] = useState<PayAssignment[] | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /**
   * Which rows have their amount field revealed.
   *
   * Overriding is DELIBERATE (an amount box that is always open is an
   * invitation to type into it), so the field is behind a control and this is
   * the set of rows whose control has been used. Keyed by row index, which is
   * how every other per-row callback on this screen is keyed.
   *
   * Revealing is not the same as overriding: a row can be open with nothing
   * typed, and it is `amount_overridden` on the row — not membership here —
   * that decides what is sent and what is drawn.
   */
  const [overrideOpen, setOverrideOpen] = useState<Set<number>>(new Set());
  /**
   * What the last save reported back, keyed by the assignment id it created.
   *
   * SESSION-SCOPED ON PURPOSE, because that is the only scope it honestly has.
   * `costing.differences` and the per-row `computeRefusal` describe THAT write;
   * neither is a column, so a reload cannot bring them back and pretending
   * otherwise would put a stale reason next to a row that has since changed.
   * What survives a reload is what the columns hold, which is the row reading
   * "not computed" with no reason attached — true, and less than we knew a
   * moment ago.
   */
  const [costingReport, setCostingReport] = useState<{
    differences: { assignmentId: string; client: { payType: string | null; calculatedAmount: number | null };
                   server: { payType: string; calculatedAmount: number; crewSize: number | null } }[];
    refusals: Record<string, string>;
    warnings: { assignmentId: string; message: string }[];
  } | null>(null);

  /**
   * The rows the LAST SAVE ATTEMPT refused, keyed `ticketLineItemId::userId`.
   *
   * Separate state from `costingReport` on purpose: that one describes a write
   * that HAPPENED and is keyed by the ids that write created. This one
   * describes a write that did not happen at all, so its rows have no ids and
   * the only handle on them is the identity the screen already holds.
   *
   * Session-scoped and cleared at the START of every save, not at the end. A
   * refusal from the previous attempt sitting beside a row the office has since
   * fixed is a reason that no longer applies, which is the same lie as a stale
   * figure.
   */
  const [saveRefusals, setSaveRefusals] = useState<Record<string, SaveRefusal> | null>(null);

  /**
   * ── THE READ THAT DECIDES WHETHER THIS SCREEN KNOWS ANYTHING ─────────────
   *
   * Both of these were `{ data, isLoading }`, which has TWO branches by
   * construction, and the third state is the one that matters most on a pay
   * screen. A 403, a 500 and a dead zone all arrive as `data: undefined`; the
   * `?? []` below turns that into an empty list; `lineItemGroups` is then
   * empty; and the empty state rendered
   *
   *     "Nothing to assign pay to — This ticket has no line items, so there is
   *      no revenue for payroll to pay out of."
   *
   * which is a claim about the TICKET made by a screen that never reached the
   * server. On an office user the API refuses, that sentence is wrong every
   * time and it is wrong in the direction that ends the investigation: the
   * reader concludes the field wrote no lines and goes to chase the field.
   *
   * `LOAD_FAILURE_QUERY_OPTIONS` is not optional garnish here. Without
   * `networkMode: 'always'` a 5xx leaves the query PAUSED — `error: null`,
   * `data: undefined`, `isLoading: false` — so a failure branch keyed on
   * `error` never renders and the empty state wins anyway. That is the shape
   * that survived AlertsScreen's first fix, and `queryLoadFailure` reads
   * `failureReason` for exactly the window in between.
   */
  const ticketQuery = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => api.tickets.get(ticketId!).then(r => r.data.data ?? r.data),
    enabled: !!ticketId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const ticketData = ticketQuery.data;
  const ticketLoading = ticketQuery.isLoading;

  // Fetch existing pay assignments
  const assignmentsQuery = useQuery({
    queryKey: ['pay-assignments', ticketId],
    queryFn: () => api.tickets.getPayAssignments(ticketId!).then(r => r.data.data ?? r.data ?? []),
    enabled: !!ticketId,
    refetchOnWindowFocus: false,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const serverAssignments = assignmentsQuery.data;
  const assignmentsLoading = assignmentsQuery.isLoading;

  /**
   * EITHER read failing makes this screen unable to say anything true.
   *
   * They are not independent: the line items come from the ticket and the pay
   * rows come from the assignments call, and a screen holding one without the
   * other can neither list what there is to pay from nor what has been paid.
   * So one notice, naming whichever did not arrive, rather than a half-drawn
   * page with a banner over it.
   *
   * The ticket is checked first because it is the one that decides the page
   * has a subject at all.
   */
  const ticketFailure = queryLoadFailure(ticketQuery);
  const assignmentsFailure = queryLoadFailure(assignmentsQuery);
  const loadFailure = ticketFailure ?? assignmentsFailure;
  const loadFailureQuery = ticketFailure ? ticketQuery : assignmentsQuery;

  // Fetch crew (field workers) for manual assignment dropdown
  const { data: crewData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
  });
  const crewMembers: { id: string; firstName: string; lastName: string; initials: string }[] = (crewData ?? [])
    .filter((u: any) => u.role === 'field_worker' || u.role === 'supervisor')
    .map((u: any) => ({
      id: u.id,
      firstName: u.firstName || u.first_name || '',
      lastName: u.lastName || u.last_name || '',
      initials: u.initials || `${(u.firstName || u.first_name || '?')[0]}${(u.lastName || u.last_name || '?')[0]}`.toUpperCase(),
    }));

  // ── The worker's configured flat rates ───────────────────────────────────
  //
  // WHY THIS SCREEN NOW FETCHES PAY RATES AT ALL. Switching a row to Day Rate
  // set `pay_type` and nothing else, so `rate_applied` stayed null, the
  // recompute below turned that null into a $0.00, and Save sent it. The API
  // inserts `calculatedAmount` verbatim — there is no server-side rate lookup on
  // POST /tickets/:id/pay-assignments — so TNT's FT-04415 holds four APPROVED
  // day_rate rows at $0.00 for a hand whose torque_monitoring day rate is $800.
  //
  // One list for the company rather than one request per row: the endpoint's
  // userId filter is optional, and the rows are small. It is gated to the same
  // roles as tier-check, so nobody who can open this screen is refused it.
  //
  // `SELECT epr.*` — snake_case keys and numerics as strings, exactly as the
  // Postgres driver hands them over. parseMoney does the coercion and turns an
  // absent column into null rather than 0.
  const { data: payRates, isLoading: payRatesLoading, isError: payRatesFailed } = useQuery({
    queryKey: ['pay-rates'],
    queryFn: () => api.payroll.getPayRates().then(r => {
      const body = r.data;
      const rows = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return Array.isArray(rows) ? rows : [];
    }),
    refetchOnWindowFocus: false,
  });

  /**
   * The rate this worker is configured to be paid for this line item type on
   * this basis — or the reason there is not one.
   *
   * Four answers, and the screen treats them differently on purpose:
   *   found   — the figure is filled in and the office sees where it came from.
   *   none    — the rate list loaded and holds nothing for this pairing. The
   *             row REFUSES: the field stays empty, the amount renders as
   *             absent, and a notice says so. Nothing writes a 0.
   *   loading — not known yet. Nothing is filled and nothing is said.
   *   error   — the list could not be fetched. A failed lookup is not proof
   *             that no rate exists, so it does not read as a refusal; it says
   *             the rates could not be loaded, which is the true statement.
   *
   * Matched on the exact `line_item_type`, the same way the tier-check route
   * resolves percentage tiers — the two must agree about which row is the
   * worker's, or the screen will fill a day rate from one row while the server
   * scores the tier from another.
   */
  const configuredRate = React.useCallback((
    userId: string,
    lineItemType: string | undefined,
    payType: string,
  ): ConfiguredRate => {
    if (payRatesFailed) return { status: 'error' };
    if (payRatesLoading || !payRates) return { status: 'loading' };
    const row = payRates.find((r: any) =>
      String(r.user_id ?? r.userId) === userId
      && String(r.line_item_type ?? r.lineItemType) === String(lineItemType ?? '')
    );
    if (!row) return { status: 'none' };
    const raw = payType === 'day_rate'
      ? (row.day_rate_amount ?? row.dayRateAmount)
      : (row.hourly_rate_amount ?? row.hourlyRateAmount);
    const amount = parseMoney(raw);
    // A configured row whose day_rate_amount is NULL or 0 has no day rate on
    // it; pay-ai and payroll both read `> 0` as "has one", and so does this.
    return amount != null && amount > 0 ? { status: 'found', amount } : { status: 'none' };
  }, [payRates, payRatesLoading, payRatesFailed]);

  // Use local state if we have edits, otherwise server data
  const assignments: PayAssignment[] = React.useMemo(
    () => localAssignments ?? serverAssignments ?? [],
    [localAssignments, serverAssignments]
  );

  // ── Stale crew-size tier detection ────────────────────────────────────────
  //
  // WHY THE SERVER SCORES THIS AND NOT THIS SCREEN. The tier a row should carry
  // is `requiredTierForCrew(crewSize)` resolved against that worker's
  // `employee_pay_rates` row — and this screen has never fetched pay rates. It
  // knows the crew count and cannot turn a tier NAME into a percentage. Putting
  // a second copy of the tier rule here is also the exact drift
  // src/lib/crew-tier.ts was created to end.
  //
  // The BODY carries the rows, saved or not, because the bug happens before
  // Save: the AI's suggestions live in local state, so a delete the office makes
  // beforehand is invisible to a server reading its own table.
  const tierSignature = React.useMemo(
    () => JSON.stringify(assignments.map(a => [
      a.ticket_line_item_id, a.user_id, a.pay_type, a.percentage_applied, a.approved,
    ])),
    [assignments]
  );
  // Debounced so typing in the percentage box does not fire a request per
  // keystroke. The signature is what settles, not the array identity.
  const [settledSignature, setSettledSignature] = useState(tierSignature);
  React.useEffect(() => {
    const t = setTimeout(() => setSettledSignature(tierSignature), 400);
    return () => clearTimeout(t);
  }, [tierSignature]);

  const { data: tierFindings } = useQuery<TierFinding[]>({
    queryKey: ['pay-tier-check', ticketId, settledSignature],
    queryFn: () => api.tickets
      .tierCheckPayAssignments(ticketId!, {
        assignments: JSON.parse(settledSignature).map(
          ([ticketLineItemId, userId, payType, percentageApplied, approved]: any[]) =>
            ({ ticketLineItemId, userId, payType, percentageApplied, approved })
        ),
      })
      .then(r => (r.data.data ?? r.data)?.findings ?? []),
    enabled: !!ticketId && JSON.parse(settledSignature).length > 0,
    // A failed check must not look like a clean bill of health, and it must not
    // shout either: `findingFor` returns undefined and no row renders a flag.
    // Same reason the query is not retried aggressively — this is advisory.
    retry: 1,
    refetchOnWindowFocus: false,
  });

  /**
   * The finding for a row, but ONLY while the findings describe the rows on
   * screen right now.
   *
   * Findings are keyed by position in the array that was submitted. The moment
   * the office deletes a row, every index after it shifts, and a stale response
   * would paint one worker's flag onto another worker's row. Comparing the
   * settled signature against the live one is what makes that impossible — an
   * in-flight check simply shows nothing until it lands.
   */
  const findingsAreCurrent = settledSignature === tierSignature;
  const findingFor = (index: number): TierFinding | undefined =>
    findingsAreCurrent ? tierFindings?.find(f => f.index === index) : undefined;

  /**
   * Fill a newly added row's percentage with the worker's OWN tier for the
   * current crew size.
   *
   * This is the one place a figure is written, and it is a default rather than
   * a recalculation: it only ever touches a row whose percentage is still null,
   * which is the state `addManualAssignment` creates and nothing else does. A
   * row the office has typed into is never null, so this cannot reach one.
   *
   * A blank tier seeds NOTHING — the server returns `seed: null` rather than a
   * neighbouring tier, and the field stays empty for the office to fill. There
   * is no ladder behind the tiers; substituting one multiplies a constant pool.
   */
  React.useEffect(() => {
    if (!findingsAreCurrent || !tierFindings || !localAssignments) return;
    const seeds = tierFindings.filter(
      f => f.status === 'unseeded' && f.seed && localAssignments[f.index]?.percentage_applied == null
    );
    if (seeds.length === 0) return;
    setLocalAssignments(prev => {
      if (!prev) return prev;
      const next = [...prev];
      for (const f of seeds) {
        const row = next[f.index];
        if (!row || row.percentage_applied != null) continue;
        const pct = f.seed!.percentageApplied;
        next[f.index] = {
          ...row,
          pay_type: f.seed!.payType,
          percentage_applied: pct,
          calculated_amount: (row.base_amount ?? row.line_item_total ?? 0) * pct,
          // A seed is the worker's own percentage tier, which is exactly what
          // needsRate[] said was missing — added since AI Suggest ran.
          needs_rate: false,
        };
      }
      return next;
    });
  }, [tierFindings, findingsAreCurrent, localAssignments]);

  // The ticket's own line items — the roster of what pay can be assigned FROM.
  // GET /tickets/:id already returns them ordered by display_order, which is the
  // same order GET /:id/pay-assignments sorts its rows by, so the two agree.
  const ticketLineItems: TicketLineItem[] = useMemo(() => {
    const raw = ticketData?.lineItems ?? ticketData?.line_items;
    return Array.isArray(raw) ? raw : [];
  }, [ticketData]);

  /**
   * Group by LINE ITEM, not by assignment.
   *
   * Iterating assignments made a line that pays nobody render no row at all —
   * and the "+ Add worker" control lives inside a group, so the one line that
   * most needed a name by hand was the one line with no door to add it. The API
   * path (POST /tickets/:id/pay-assignments) was open the whole time.
   *
   * payroll.service.ts walks EVERY line item on an approved ticket and pushes an
   * info flag for each one with no approved assignments, so "no assignments" is
   * a state the backend already has an opinion about — it is not an error, and
   * it is about to be common rather than rare.
   */
  const lineItemGroups = useMemo(() => {
    const byLineItem = new Map<string, PayAssignment[]>();
    for (const a of assignments) {
      const list = byLineItem.get(a.ticket_line_item_id);
      if (list) list.push(a);
      else byLineItem.set(a.ticket_line_item_id, [a]);
    }

    const groups: LineItemGroup[] = [];
    const claimed = new Set<string>();

    for (const li of ticketLineItems) {
      const key = li.id ? String(li.id) : '';
      // A line item with no id cannot be assigned against — POST
      // /pay-assignments keys on ticketLineItemId — so a row for it would be a
      // row whose only control is broken. Skip rather than render a dead one.
      if (!key || claimed.has(key)) continue;
      claimed.add(key);
      groups.push({
        lineItemId: key,
        type: li.line_item_type || li.lineItemType || 'Service',
        description: li.description || 'Line Item',
        // numeric columns arrive as strings; parseMoney rejects null/undefined/''
        // BEFORE coercing, which Number()||0 cannot do — Number(null) is 0.
        total: parseMoney(li.total),
        rate: li.rate != null ? Number(li.rate) : null,
        quantity: li.quantity != null ? Number(li.quantity) : null,
        assignments: byLineItem.get(key) ?? [],
        // The line's own answer first (it exists before anyone is assigned),
        // then any row's — the two come from the same API rule.
        shiftsRule: shiftsRuleOf(li.shifts_rule ?? li.shiftsRule)
          ?? shiftsRuleOf((byLineItem.get(key) ?? []).find(a => a.shifts_rule)?.shifts_rule),
        techSlots: techSlotsOf(li),
      });
    }

    // An assignment whose line item is not in the ticket payload still gets a
    // row: AI suggestions land in local state before any ticket refetch, and pay
    // attached to a since-deleted line item must not vanish silently. Metadata
    // falls back to the assignment's own joined tli.* columns — which is exactly
    // where every group used to get it.
    byLineItem.forEach((list, key) => {
      if (claimed.has(key)) return;
      const a = list[0];
      groups.push({
        lineItemId: key,
        type: a.line_item_type || 'Service',
        description: a.line_item_description || 'Line Item',
        // Same honesty as the branch above — the two must agree, or the same
        // absent total renders '—' on one row and '$0.00' on the next.
        total: parseMoney(a.line_item_total),
        rate: a.line_item_rate ?? null,
        quantity: a.line_item_quantity ?? null,
        assignments: list,
        shiftsRule: shiftsRuleOf(list.find(a => a.shifts_rule)?.shifts_rule),
        // The pay-assignment join selects tli.line_item_type/description/total/
        // rate/quantity and nothing else, so there are no slots to read here.
        // An empty list means "unknown", and the render treats it that way.
        techSlots: [],
      });
    });

    return groups;
  }, [assignments, ticketLineItems]);

  /**
   * Does any line on this ticket name crew in its tech slots?
   *
   * Drives the prompt bar's copy, and it has to: "AI can suggest pay from the
   * crew named on each line" is simply false on a ticket where nothing names
   * anybody, and it would sit directly above a column of "Pays nobody" rows
   * saying the opposite.
   *
   * THE FALSE HALF USED TO BE THE OTHER ONE. This branch read "so AI will match
   * from configured pay rates instead", which was true of API #422-era pay-ai
   * and is now false: that second path — match every crew member holding a rate
   * for the line's type — has been deleted. Jason, 2026-08-27: if nobody is
   * named on a line, nobody is paid on it. So the bar no longer promises a
   * fallback that will not run; it names the thing that actually moves the
   * ticket forward, which is a person on the line.
   *
   * THE BUTTON STAYS. In this state AI Suggest returns zero suggestions for
   * every payable line, so it is arguably a control that cannot do anything —
   * the reason #458 dropped the centred call to action. It is kept because the
   * bar's OTHER state is one keypress away (name one hand and AI does the rest
   * of the ticket), and because a button that visibly does nothing is a
   * cheaper way to learn the rule than a missing one. Removing it is an
   * operator call, not a drive-by.
   */
  const anyLineNamesCrew = useMemo(
    () => lineItemGroups.some((g) => g.techSlots.length > 0),
    [lineItemGroups],
  );

  // One assignment the server has not costed makes the crew's total unknown,
  // not smaller. fmtCurrency renders the null as '\u2014'.
  const totalPay = useMemo(() => sumMoney(assignments.map((a) => a.calculated_amount)), [assignments]);
  // Counted over every row including the approved ones: an approved override is
  // still an amount a person set, and it is the one a reviewer most wants
  // counted — it can no longer be edited.
  const overriddenTotal = useMemo(() => overriddenCount(assignments), [assignments]);
  /**
   * The company's users, for turning `amount_overridden_by` into a name.
   *
   * The UNFILTERED list, not `crewMembers`: that one is narrowed to field
   * workers and supervisors because it feeds the "add a worker" picker, and an
   * override is written by admin or office_staff — the two roles the picker
   * excludes. Resolving the author against it would miss every author there is.
   */
  const allUsers: { id: string; firstName?: string; lastName?: string; first_name?: string; last_name?: string }[] =
    Array.isArray(crewData) ? crewData : [];
  const allApproved = assignments.length > 0 && assignments.every(a => a.approved);
  /**
   * Rows the API will approve. `POST /pay-assignments/approve` touches only
   * `approved = false` rows, so a partially approved ticket still has work for
   * Approve All to do — it used to be disabled the moment ANY row was approved.
   */
  const unapprovedCount = assignments.filter(a => !a.approved).length;

  // Rows the AI wasn't confident about — these must be eyeballed before they hit payroll.
  const needsReview = assignments.filter(a => a.ai_confidence === 'low' || a.ai_confidence === 'medium');
  // The roll-up can never read higher than the weakest row still on screen.
  const rollUpConfidence: ConfidenceLevel | null = aiResult
    ? lowestConfidence(assignments.map(a => a.ai_confidence), aiResult.overallConfidence)
    : null;

  // Auto-expand all on first load
  React.useEffect(() => {
    if (lineItemGroups.length > 0 && expandedItems.size === 0) {
      setExpandedItems(new Set(lineItemGroups.map(g => g.lineItemId)));
    }
  }, [lineItemGroups]);

  // Flash message auto-dismiss — except for the ones that are a finding.
  React.useEffect(() => {
    if (message && !message.sticky) {
      const t = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  // AI Suggest mutation
  const aiMutation = useMutation({
    mutationFn: () => api.tickets.aiSuggestPay(ticketId!),
    onSuccess: (res) => {
      const result: AiResult = res.data.data ?? res.data;
      setAiResult(result);

      // Convert AI suggestions into local assignment rows
      const newAssignments: PayAssignment[] = result.suggestions.map((s) => {
        const [firstName, ...lastParts] = s.employeeName.split(' ');
        return {
          ticket_line_item_id: s.ticketLineItemId,
          user_id: s.userId,
          first_name: firstName,
          last_name: lastParts.join(' '),
          initials: s.employeeInitials || getInitials(firstName, lastParts.join(' ')),
          pay_type: s.payType,
          percentage_applied: s.percentageApplied,
          rate_applied: s.rateApplied,
          base_amount: s.lineItemTotal,
          calculated_amount: s.calculatedAmount,
          role_on_ticket: s.roleOnTicket,
          approved: false,
          line_item_type: s.lineItemType,
          line_item_description: s.lineItemDescription || undefined,
          line_item_total: s.lineItemTotal ?? undefined,
          ai_confidence: s.confidence,
          ai_reasoning: s.reasoning,
        };
      });

      // A hand the AI could not pay because the line needs shifts still gets a
      // row — on a day rate with no amount and the shifts box open — or the
      // hand would simply vanish from the screen. Rate from their own card.
      const shiftRows: PayAssignment[] = (Array.isArray(result.needsShifts) ? result.needsShifts : [])
        .filter(n => !newAssignments.some(a => a.ticket_line_item_id === n.ticketLineItemId && a.user_id === n.userId))
        .map((n) => {
          const [firstName, ...lastParts] = n.employeeName.split(' ');
          const group = lineItemGroups.find(g => g.lineItemId === n.ticketLineItemId);
          const found = configuredRate(n.userId, group?.type, 'day_rate');
          return {
            ticket_line_item_id: n.ticketLineItemId,
            user_id: n.userId,
            first_name: firstName,
            last_name: lastParts.join(' '),
            initials: getInitials(firstName, lastParts.join(' ')),
            pay_type: 'day_rate',
            percentage_applied: null,
            rate_applied: found.status === 'found' ? found.amount : null,
            base_amount: null,
            calculated_amount: null,
            role_on_ticket: null,
            approved: false,
            line_item_type: group?.type,
            line_item_description: n.lineItemDescription || undefined,
            shifts_rule: group?.shiftsRule ?? 'entry_required',
            shifts_resolution: { kind: 'refused', reason: n.reason, message: n.message },
          };
        });

      // Same for a hand on an equipment line with no percentage for its
      // type: a row with no percentage and no amount, flagged, or the hand
      // vanishes the way needs-shifts hands did before fe#1112. Never a day
      // rate — the line does not pay one (api#1184).
      const rateRows: PayAssignment[] = (Array.isArray(result.needsRate) ? result.needsRate : [])
        .filter(n => ![...newAssignments, ...shiftRows].some(a => a.ticket_line_item_id === n.ticketLineItemId && a.user_id === n.userId))
        .map((n) => {
          const [firstName, ...lastParts] = n.employeeName.split(' ');
          const group = lineItemGroups.find(g => g.lineItemId === n.ticketLineItemId);
          return {
            ticket_line_item_id: n.ticketLineItemId,
            user_id: n.userId,
            first_name: firstName,
            last_name: lastParts.join(' '),
            initials: getInitials(firstName, lastParts.join(' ')),
            pay_type: 'percentage_single',
            percentage_applied: null,
            rate_applied: null,
            base_amount: group?.total ?? null,
            calculated_amount: null,
            role_on_ticket: null,
            approved: false,
            line_item_type: group?.type,
            line_item_description: n.lineItemDescription || undefined,
            line_item_total: group?.total ?? undefined,
            needs_rate: true,
          };
        });

      // Merge: keep approved server assignments, replace unapproved with AI suggestions
      const kept = (serverAssignments ?? []).filter((a: PayAssignment) => a.approved);
      setLocalAssignments([...kept, ...newAssignments, ...shiftRows, ...rateRows]);
      setDirty(true);
      setExpandedItems(new Set([...newAssignments, ...shiftRows, ...rateRows].map(a => a.ticket_line_item_id)));
      // Never report a roll-up higher than the weakest individual suggestion.
      const effective = lowestConfidence(result.suggestions.map(s => s.confidence), result.overallConfidence);
      setMessage({ type: 'success', text: `AI generated ${result.suggestions.length} pay suggestions (${effective} confidence)` });
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'AI pay suggestion failed' });
    },
  });

  /**
   * Save.
   *
   * ── WHAT THE CLIENT STILL DECIDES, AND WHAT IT NO LONGER DOES ────────────
   *
   * API #537 made the server cost every row. The figures in this body —
   * `percentageApplied`, `rateApplied`, `baseAmount`, `calculatedAmount` — are
   * NOT what gets stored on an ordinary row; the server prices it from the
   * worker's rate row, the line and the tech slots. The client still chooses
   * the BASIS (`payType`), because a worker can hold both a percentage tier and
   * a day rate and only the office knows which applies.
   *
   * They are still SENT, deliberately, and this is the part that reads like
   * dead weight and is not: the API compares `calculatedAmount` against what it
   * computed and returns the gap in `costing.differences`, expressly so a
   * screen can tell the office its number was replaced rather than let them
   * discover it on the next fetch. Stop sending the number and that report goes
   * quiet — not because the numbers agree, but because there is nothing left to
   * compare. The office's figure is the question; the report is the answer.
   *
   * ── AND WHERE THE OFFICE'S NUMBER IS MEANT TO WIN ────────────────────────
   *
   * `overrideAmount: true` alongside `calculatedAmount`. An amount on its own is
   * never an override — that is exactly the shape that used to arrive by
   * accident from a screen that recomputed locally — so the flag is explicit and
   * the server refuses the pair when it is half-formed. Only rows the office
   * deliberately overrode carry it.
   */
  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = assignments
        .filter(a => !a.approved) // Only send unapproved (approved are already saved)
        .map(a => ({
          ticketLineItemId: a.ticket_line_item_id,
          userId: a.user_id,
          payType: a.pay_type,
          percentageApplied: a.percentage_applied,
          rateApplied: a.rate_applied,
          baseAmount: a.base_amount,
          calculatedAmount: a.calculated_amount,
          // `roleOnTicket` is NOT sent. The server derives it from the line item
          // and reports it back under `costing.ignored` if a caller sends one.
          // Absent, not `false`, on an ordinary row: the server reads the flag's
          // presence and an explicit false is the CLEAR instruction on PATCH.
          // Sending it here on every row would be saying something on behalf of
          // rows nobody decided anything about.
          ...(a.amount_overridden === true ? { overrideAmount: true } : {}),
          // Only a figure the office entered. Absent is "not entered" and lets
          // the server decide (one hand / even split); it is never sent as 0.
          ...(parseShifts(a.shifts_worked) != null ? { shiftsWorked: parseShifts(a.shifts_worked) } : {}),
        }));
      return api.tickets.createPayAssignments(ticketId!, { assignments: payload, replaceExisting: true });
    },
    // Both reports describe ONE write. Clearing them here rather than in the
    // handlers means a failed attempt cannot leave last time's differences on
    // screen, and a successful one cannot leave last time's refusals.
    onMutate: () => {
      setCostingReport(null);
      setSaveRefusals(null);
    },
    onSuccess: (res: any) => {
      setDirty(false);
      setLocalAssignments(null);
      setOverrideOpen(new Set());
      queryClient.invalidateQueries({ queryKey: ['pay-assignments', ticketId] });

      // WHAT THE SERVER DID WITH IT. `costing` is the half of this response the
      // screen used to throw away, and throwing it away is what let the office
      // type a figure, watch it be accepted, and be paid another one.
      const costing = res?.data?.costing ?? {};
      const differences = Array.isArray(costing.differences) ? costing.differences : [];
      const warnings = Array.isArray(costing.warnings) ? costing.warnings : [];
      const overridden = Array.isArray(costing.overridden) ? costing.overridden : [];
      const refusals: Record<string, string> = {};
      for (const o of overridden) {
        if (o?.assignmentId && o?.computeRefusal?.message) refusals[o.assignmentId] = o.computeRefusal.message;
      }
      setCostingReport({ differences, refusals, warnings });

      // The count leads, and the wording says which way the replacement went.
      // "Saved successfully" over the top of a replaced figure is the same
      // silence this screen is being fixed for.
      if (differences.length > 0) {
        setMessage({
          type: 'error',
          text: `Saved. ${differences.length} row${differences.length === 1 ? '' : 's'} `
              + `${differences.length === 1 ? 'was' : 'were'} priced by the server and `
              + `${differences.length === 1 ? 'does' : 'do'} not match what was on screen — `
              + `the stored figures are shown below. To make a figure of your own stand, override it.`,
          sticky: true,
        });
      } else {
        const n = overridden.length;
        setMessage({
          type: 'success',
          text: n > 0
            ? `Pay assignments saved · ${n} amount${n === 1 ? '' : 's'} set by hand`
            : 'Pay assignments saved successfully',
        });
      }
    },
    /**
     * A REFUSAL NAMES ITS ROWS, AND THIS IS WHERE THAT STOPPED BEING TRUE.
     *
     * The 400 body carries `refusals[]` — `ticketLineItemId`, `userId`, `reason`
     * and `message` per row — and this handler read only `error`, the single
     * string the route builds by joining every message with " | ". On TNT's
     * fifteen-row tickets that is close to a thousand characters of prose with
     * no way to tell which row each clause is about, and it was not sticky, so
     * it erased itself after five seconds.
     *
     * Nothing was saved: the route returns before the insert. The banner says
     * so, and every refused row is marked below with the server's own words.
     */
    onError: (err: any) => {
      const raw: any[] = Array.isArray(err?.response?.data?.refusals)
        ? err.response.data.refusals : [];

      // The request body is the unapproved rows, in order — the same filter the
      // payload is built from. It is the only handle on an `invalid_override`
      // refusal, which carries `index` and neither id.
      const sent = assignments.filter(a => !a.approved);
      const marked: Record<string, SaveRefusal> = {};
      const rows: SaveRefusal[] = [];
      for (const r of raw) {
        const fallback = typeof r?.index === 'number' ? sent[r.index] : undefined;
        const ticketLineItemId = r?.ticketLineItemId ?? fallback?.ticket_line_item_id;
        const userId = r?.userId ?? fallback?.user_id;
        const message = typeof r?.message === 'string' ? r.message : '';
        if (!message) continue;
        const row: SaveRefusal = { ticketLineItemId, userId, reason: String(r?.reason ?? ''), message };
        rows.push(row);
        if (ticketLineItemId && userId) marked[refusalKey(ticketLineItemId, userId)] = row;
      }
      setSaveRefusals(rows.length > 0 ? marked : null);

      // A 400 that is not a costing refusal — foreign ids, splits over 100% —
      // still has only the one sentence, and it is still the whole answer.
      if (rows.length === 0) {
        setMessage({
          type: 'error',
          text: err.response?.data?.error ?? 'Failed to save assignments',
          sticky: true,
        });
        return;
      }

      const n = rows.length;
      const count = (r: RefusalRemedy) => rows.filter(x => REFUSAL_REMEDY[x.reason] === r).length;
      const overrideN = count('override');
      const payTypeN = count('pay_type');
      const rateCardN = count('rate_card');

      let text = `Nothing was saved. ${n} row${n === 1 ? '' : 's'} could not be costed, and `
               + `${n === 1 ? 'it is' : 'each one is'} marked below with the reason.`;
      // THE REMEDY, WHERE THERE IS ONE, AND ONLY WHERE IT IS THE RIGHT ONE.
      // Until now the only copy on this screen mentioning the override lived on
      // the SUCCESS path, which a blocked office never reaches.
      if (overrideN > 0) {
        text += ` The crew on ${overrideN === 1 ? 'a line' : 'some lines'} cannot be counted, and no rate card fixes that`
              + ` — use "Override amount" on ${overrideN === 1 ? 'that row' : 'those rows'} to make a figure of your own stand.`;
      }
      if (payTypeN > 0) {
        text += ` ${payTypeN === 1 ? 'One row is' : `${payTypeN} rows are`} on a basis the worker is not paid on`
              + ` — switch ${payTypeN === 1 ? 'it' : 'them'} to Day Rate, or add the percentage tiers under the`
              + ` employee's pay rates. Do not override ${payTypeN === 1 ? 'it' : 'them'}: the basis is what is wrong,`
              + ` and a typed amount hides that instead of fixing it.`;
      }
      const shiftsN = count('shifts');
      if (shiftsN > 0) {
        text += ` ${shiftsN === 1 ? 'One row needs' : `${shiftsN} rows need`} shifts per hand that add up to the line quantity.`;
      }
      if (rateCardN > 0) {
        text += ` ${rateCardN === 1 ? 'One row has' : `${rateCardN} rows have`} no rate to calculate from`
              + ` — add it under the employee's pay rates.`;
      }
      const percentageN = count('percentage');
      if (percentageN > 0) {
        text += ` ${percentageN === 1 ? 'One row is' : `${percentageN} rows are`} on an equipment line with no`
              + ` percentage set for its type — set it in pay rates or enter an amount.`;
      }
      setMessage({ type: 'error', text, sticky: true });
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: () => api.tickets.approvePayAssignments(ticketId!),
    onSuccess: () => {
      setLocalAssignments(null);
      queryClient.invalidateQueries({ queryKey: ['pay-assignments', ticketId] });
      setMessage({ type: 'success', text: 'All pay assignments approved' });
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'Failed to approve assignments' });
    },
  });

  // Leaving with unapproved pay is allowed, but not silent. Not while an
  // approve is already on its way — that pay is about to reach payroll.
  useUnapprovedPayLeaveNotice(unapprovedCount > 0 && !approveMutation.isPending);

  /**
   * Unapprove — the fifth named mutation, and the one that was a raw call.
   *
   * IT RAN INLINE IN AN onClick UNTIL NOW, and fe#737 left it that way on
   * purpose: it was a convenience on a banner, the screen's real writes were
   * the four below, and lifting it would have been churn.
   *
   * WHAT CHANGED IS ITS STANDING, NOT ITS BEHAVIOUR. api#774 refuses to delete
   * a ticket that carries approved pay and points the user at this screen, so
   * this button is now the ONLY door out of an approved pay assignment
   * anywhere in the product. A door that is the only one gets the same
   * treatment as the rest:
   *
   *   - `isPending`, because the inline version had none. Nothing disabled the
   *     button while the request was open, so a second click fired a second
   *     unapprove against a ticket the first had already unapproved — and the
   *     API answers the second one differently;
   *   - the server's own message on failure. The inline `catch {}` discarded
   *     the error object entirely and wrote a fixed string, so a refusal, a
   *     validation failure and a dead connection were one sentence;
   *   - the same invalidation key the approve path uses, so the two are
   *     provably a pair rather than coincidentally similar.
   *
   * `setLocalAssignments(null)` drops any in-progress local edit, exactly as
   * approve does — the rows are about to be refetched and a stale local copy
   * would win over the server's.
   */
  const unapproveMutation = useMutation({
    mutationFn: () => api.tickets.unapprovePayAssignments(ticketId!),
    onSuccess: () => {
      setLocalAssignments(null);
      queryClient.invalidateQueries({ queryKey: ['pay-assignments', ticketId] });
      setMessage({ type: 'success', text: 'Pay assignments unlocked for editing' });
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'Failed to unapprove' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (assignmentId: string) => api.tickets.deletePayAssignment(ticketId!, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-assignments', ticketId] });
      setMessage({ type: 'success', text: 'Assignment deleted' });
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'Failed to delete assignment' });
    },
  });

  // Local editing helpers
  function updateLocalAssignment(index: number, updates: Partial<PayAssignment>) {
    const updated = [...assignments];
    const before = updated[index];
    const a = { ...before, ...updates };

    // A change of BASIS fills the rate from the worker's own configured row, or
    // leaves it empty when there is none — the only two things this can do. It
    // never keeps the previous basis's figure: an hourly $75 carried onto a Day
    // Rate row is a plausible-looking wrong number, which is the whole hazard.
    // A rate the office types afterwards goes through the branch below this
    // one, untouched, because `updates.pay_type` is absent on that call.
    const basisChanged = updates.pay_type !== undefined && updates.pay_type !== before.pay_type;
    const toFlat = a.pay_type === 'day_rate' || a.pay_type === 'hourly';
    const fromFlat = before.pay_type === 'day_rate' || before.pay_type === 'hourly';
    if (basisChanged && toFlat) {
      const lineType = lineItemGroups.find(g => g.lineItemId === a.ticket_line_item_id)?.type
        ?? a.line_item_type;
      const found = configuredRate(a.user_id, lineType, a.pay_type);
      a.rate_applied = found.status === 'found' ? found.amount : null;
      // The percentage does not ride along on a day-rate row. POST
      // /pay-assignments sums every `percentageApplied` it is sent toward the
      // line's 100% cap without looking at `payType`, so a stale 10% here
      // counts against the hands who are actually on a percentage.
      a.percentage_applied = null;
    } else if (basisChanged && fromFlat && !toFlat) {
      // And the day rate does not ride along on a percentage row. The tier
      // seed fills the empty percentage from the worker's own tier, the same
      // way it does for a newly added hand.
      a.rate_applied = null;
    }

    // Auto-recalculate amount. Absent stays absent: a missing rate, percentage
    // or hour count makes the amount unknown, not $0.00. The `?? 0` this
    // replaced is how four approved $0.00 rows reached payroll.
    //
    // AN OVERRIDDEN ROW IS NOT RECOMPUTED. Its amount is a decision somebody
    // made and recorded, and every other edit on this row — a role rename, a
    // basis change — must leave that decision standing. This is the same rule
    // the API's PATCH keeps ("an EXISTING override is PRESERVED"), and it has
    // to be kept in both places: the recompute below runs on the office's own
    // keystrokes, long before anything is sent. Clearing is its own control.
    const money = (v: number | null | undefined) => (v == null ? null : Number(v));
    if (a.amount_overridden === true) {
      // deliberately nothing — `calculated_amount` is the office's figure
    } else if (isPercentagePayType(a.pay_type)) {
      const base = money(a.base_amount ?? a.line_item_total);
      const pct = money(a.percentage_applied);
      a.calculated_amount = base != null && pct != null ? base * pct : null;
    } else if (a.pay_type === 'day_rate'
        && takesShifts(lineItemGroups.find(g => g.lineItemId === a.ticket_line_item_id)?.shiftsRule ?? null)) {
      // A per-day labour line: the server pays day rate × shifts, and the
      // shifts are its call (single hand, even split, or the office's own).
      // Not worked out here — the amount is absent until Save brings the
      // server's figure back, and the row says so.
      a.calculated_amount = null;
      a.shifts_stale = true;
    } else if (a.pay_type === 'day_rate') {
      // The LINE prices the row: subsistence is rate × person-days ÷ the hands
      // named on it, and a line naming nobody cannot be shared out, so the
      // amount stays absent rather than dividing by an invented 1. Every other
      // flat type is the rate itself. Same helper the row renders from.
      const share = priceDayRate(lineItemGroups.find(g => g.lineItemId === a.ticket_line_item_id), a);
      a.calculated_amount = share?.status === 'priced' ? share.earnings : null;
    } else if (a.pay_type === 'hourly') {
      const rate = money(a.rate_applied);
      const hours = money(a.line_item_quantity ?? ticketData?.hours);
      a.calculated_amount = rate != null && hours != null ? rate * hours : null;
    }

    updated[index] = a;
    setLocalAssignments(updated);
    setDirty(true);
  }

  /**
   * Set one hand's shifts on a per-day labour line.
   *
   * Every row on the LINE goes stale, not just this one: the server's answer
   * for each hand depends on what the others entered (they must add up to the
   * quantity), so a resolution from before this keystroke describes a split
   * that is no longer on screen. Their amounts go absent until Save.
   */
  function setShifts(index: number, value: number | null) {
    const lineId = assignments[index]?.ticket_line_item_id;
    const updated = assignments.map((a, i) => {
      if (i === index) {
        return {
          ...a,
          shifts_worked: value,
          shifts_stale: true,
          calculated_amount: a.amount_overridden === true ? a.calculated_amount : null,
        };
      }
      if (a.ticket_line_item_id === lineId && !a.approved && a.pay_type === 'day_rate') {
        return { ...a, shifts_stale: true, calculated_amount: a.amount_overridden === true ? a.calculated_amount : null };
      }
      return a;
    });
    setLocalAssignments(updated);
    setDirty(true);
  }

  /**
   * Set this row's amount by hand.
   *
   * The office's figure becomes `calculated_amount` — WHAT PAYS — and the row
   * is flagged. Author and timestamp are the SERVER's to write: it stamps the
   * caller's own id and its own clock, and a client that guessed at either
   * would be recording a decision under a name it made up. So they stay absent
   * here and arrive on the refetch.
   *
   * An absent amount clears the flag rather than storing a half-decision. The
   * API refuses `overrideAmount: true` with no amount, and the database refuses
   * an override with no figure; a row that reached either in that shape would
   * be a 400 the office did not cause.
   */
  function setOverrideAmount(index: number, amount: number | null) {
    const updated = [...assignments];
    const a = { ...updated[index] };
    if (amount === null || !Number.isFinite(amount)) {
      a.amount_overridden = false;
      a.calculated_amount = null;
    } else {
      a.amount_overridden = true;
      a.calculated_amount = amount;
    }
    updated[index] = a;
    setLocalAssignments(updated);
    setDirty(true);
  }

  /**
   * Put the row back on the server's figure.
   *
   * Without this an override is one-way: the flag survives every subsequent
   * edit by design, so nothing else on the screen can take it off. The computed
   * figure is restored as the amount where there IS one; where the costing
   * refused there is none, and the amount goes back to absent rather than to
   * the number that was just disowned.
   */
  function clearOverride(index: number) {
    const updated = [...assignments];
    const a = { ...updated[index] };
    a.amount_overridden = false;
    a.amount_overridden_by = null;
    a.amount_overridden_at = null;
    a.calculated_amount = a.computed_amount == null ? null : Number(a.computed_amount);
    updated[index] = a;
    setLocalAssignments(updated);
    setOverrideOpen(prev => { const n = new Set(prev); n.delete(index); return n; });
    setDirty(true);
  }

  function toggleOverrideOpen(index: number) {
    setOverrideOpen(prev => {
      const n = new Set(prev);
      if (n.has(index)) n.delete(index); else n.add(index);
      return n;
    });
  }

  function removeLocalAssignment(index: number) {
    const a = assignments[index];
    // If it has a server ID, delete via API
    if (a.id) {
      deleteMutation.mutate(a.id);
    }
    const updated = assignments.filter((_, i) => i !== index);
    setLocalAssignments(updated.length > 0 ? updated : []);
    setDirty(true);
  }

  function addManualAssignment(lineItemId: string, userId: string) {
    const crew = crewMembers.find(c => c.id === userId);
    if (!crew) return;
    const group = lineItemGroups.find(g => g.lineItemId === lineItemId);
    if (!group) return;

    const newAssignment: PayAssignment = {
      ticket_line_item_id: lineItemId,
      user_id: userId,
      first_name: crew.firstName,
      last_name: crew.lastName,
      initials: crew.initials,
      // NOT `percentage_single` at 0, which is what this seeded before. Both
      // halves were wrong: the tier ignored the crew the worker was joining, and
      // a 0 renders as a real "0.0%" and a real $0.00 rather than as an empty
      // field. The tier-check effect above fills both from the worker's own rate
      // for the CURRENT crew size; until it lands, absent renders as absent.
      pay_type: 'percentage_single',
      percentage_applied: null,
      rate_applied: null,
      base_amount: group.total,
      line_item_total: group.total,
      calculated_amount: null,
      // NOT 'Lead Tech', which is what this seeded for every line item type —
      // so a wash_truck or laser_tally row was filed under a tong role, and the
      // label reached timesheet_entries and the accounting CSV from there.
      // The server derives it from the line on Save (formatRoleForLineItem);
      // until then the row has no label and absent renders as absent.
      role_on_ticket: null,
      approved: false,
      line_item_type: group.type,
      line_item_description: group.description,
      // The line's own quantity, so a later switch to Hourly has hours to
      // multiply by; the server-side join carries the same column.
      line_item_quantity: group.quantity ?? undefined,
    };
    const updated = [...assignments, newAssignment];
    setLocalAssignments(updated);
    setDirty(true);
  }

  function toggleExpanded(id: string) {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isLoading = ticketLoading || assignmentsLoading;
  /**
   * Rows the server says cannot be paid until shifts are entered or corrected.
   * Approve All waits for them: approving would send a day rate paid once, or
   * a split that does not add up, to payroll.
   */
  const shiftsBlocked = assignments.filter(a =>
    !a.approved && a.pay_type === 'day_rate' && shiftsResolutionOf(a.shifts_resolution)?.kind === 'refused'
  ).length;
  /**
   * Hands AI Suggest reported in `needsRate[]`: an equipment line and no
   * percentage for its type. Approve All waits for them too — until a
   * percentage is set or the office overrides the amount, the row pays nothing
   * and approving it would send that to payroll as if it were a decision.
   */
  const rateBlocked = assignments.filter(a =>
    !a.approved && a.needs_rate === true && a.amount_overridden !== true
  ).length;
  const canApproveAll = unapprovedCount > 0 && shiftsBlocked === 0 && rateBlocked === 0
    && !approveMutation.isPending && !saveMutation.isPending;

  /**
   * The band, in one place, because three call sites render it — the failure
   * branch, the loading branch and the page — and a band that differs between
   * them is a page whose identity changes as it loads.
   */
  const band = (opts: {
    stats?: React.ComponentProps<typeof PageHeaderBand>['stats'];
    actions?: React.ReactNode;
  } = {}) => (
    <PageHeaderBand
      eyebrow="Casing payroll"
      title="Pay Assignments"
      onBack={() => navigate(-1)}
      backLabel="Back"
      actions={opts.actions}
      stats={opts.stats}
    />
  );

  /**
   * FOUR FIGURES THE TICKET STATES, and the strip is OMITTED until they are
   * knowable rather than rendered as a row of em dashes.
   *
   * A dash means "this ticket has no such figure", which is a claim, and while
   * the read is still in flight the screen is not entitled to make it. The
   * band renders with no strip at all until the ticket arrives — the same
   * choice TicketDetail made in fe#734, for the same reason.
   *
   * Every one of these is read off data already on screen; none is a second
   * answer to a question the API answers. `totalPay` is the same `sumMoney`
   * the bottom bar prints, called once and passed to both.
   *
   * Labels are kept short on purpose: the hero circle's clearance was swept
   * against Quotes' and Rate Sheets' stat text, and the third column has a
   * hard ceiling near 70px between 1024 and 1048 wide. "Assigned" measures
   * well inside it.
   */
  const bandStats: React.ComponentProps<typeof PageHeaderBand>['stats'] = ticketData
    ? [
        {
          icon: <DollarSign className="size-4" aria-hidden="true" />,
          label: 'Revenue',
          value: fmtCurrency(ticketData.total ?? ticketData.subtotal),
          sub: 'What the ticket bills',
        },
        {
          icon: <FileText className="size-4" aria-hidden="true" />,
          label: 'Lines',
          value: displayValue(lineItemGroups.length),
          sub: 'On this ticket',
        },
        {
          icon: <Users className="size-4" aria-hidden="true" />,
          label: 'Assigned',
          value: displayValue(assignments.length),
          sub: 'Pay rows',
        },
        {
          icon: <Clock className="size-4" aria-hidden="true" />,
          label: 'Pay',
          value: fmtCurrency(totalPay),
          sub: 'Total assigned',
          tone: 'accent' as const,
        },
      ]
    : undefined;

  /**
   * ── A REFUSED READ IS NOT AN EMPTY TICKET ────────────────────────────────
   *
   * Rendered in place of the whole body, not as a banner over it, because a
   * banner leaves the empty state visible underneath — "no line items, so
   * there is no revenue for payroll to pay out of" — and that sentence is the
   * bug this branch exists to remove, not something to apologise above.
   *
   * The band still renders, with NO stat strip: the page keeps its identity
   * and claims no figure it did not load.
   */
  if (loadFailure) {
    return (
      <div data-testid="pay-assignment-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        {band()}
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8">
              <LoadFailureNotice
                error={queryFailureError(loadFailureQuery)}
                /* A lowercase plural noun phrase — the helper renders it
                   mid-sentence as "not a statement about your {subject}". */
                subject="pay assignments"
                /* NO `roleLabel`. It would need `useAuth`, and this screen has
                   never consumed auth — adding the context here makes the
                   component unrenderable outside a provider, which is a
                   behaviour change wearing a nicety's clothes. The
                   refused/unreachable split is the part that decides what the
                   reader does next, and it does not depend on naming them. */
                allowed="admins and office staff"
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  /**
   * The single condition both AI Suggest controls read.
   *
   * There is one AI Suggest control on screen at a time, never two. When the
   * ticket has no pay assignments the control is the prompt bar above the list,
   * which carries the explanation of what AI will match from; once anything is
   * assigned the bar goes away and the control is the header button.
   *
   * Deriving both from this one boolean is the point. #458 removed the centred
   * empty-state call to action and the header button was deliberately left
   * alone; when the prompt bar put the call to action back, the two rendered
   * together a few pixels apart on exactly the tickets the bar exists for. Two
   * separate conditions describing the same state is how they disagreed, so
   * this is the only place that state is decided.
   *
   * The lineItemGroups clause is load-bearing, not incidental: with no line
   * items pay-ai.service.ts returns zero suggestions and "No line items on this
   * ticket", so the bar would be offering a control that provably cannot do
   * anything. That state falls to the header button, which is still the one
   * control on screen.
   */
  const showAiPromptBar = !isLoading && assignments.length === 0 && lineItemGroups.length > 0;

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div data-testid="pay-assignment-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* THE BAND, carrying the page's identity and the ticket's own figures.
          This screen previously rendered the one-line `border-b px-6 py-3`
          header; before that it carried the deprecated two-line variant. The
          band's back control is the plain arrow with no worded label, which is
          the settled idiom and overrides the Manus handoff's arrow-plus-word.

          AI SUGGEST IS A BAND ACTION, and it is the SECONDARY treatment: a
          white/25 border and NO colour class, so it inherits the band's
          near-white ink. A coloured utility here would be remapped to the
          heading colour the moment the app switches to light — the failure
          PageHeaderBand's own docblock records for a signal hue used as ink on
          the band. */}
      {band({
        stats: bandStats,
        /* Suppressed while the prompt bar is up — see showAiPromptBar. The bar
           carries its own AI Suggest, and the two used to render a few pixels
           apart on every ticket with nothing assigned yet. */
        actions: !showAiPromptBar ? (
          <button
            onClick={() => aiMutation.mutate()}
            disabled={aiMutation.isPending || allApproved}
            className="fi-action fi-action--standard inline-flex items-center gap-1.5 rounded-fi-pill border border-white/25 px-3 py-2 text-sm font-semibold transition-colors hover:bg-white/10 disabled:opacity-50"
          >
            {aiMutation.isPending ? (
              <><Loader size={14} className="animate-spin" aria-hidden="true" /> Thinking...</>
            ) : (
              <><Sparkles size={14} aria-hidden="true" /> AI Suggest</>
            )}
          </button>
        ) : undefined,
      })}

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {/* ── Which ticket, and how many of its amounts a person set by hand ──
            The ticket number moved off the title line and into the body: the
            band's title is the PAGE, and the record it is about belongs with
            the record.

            The overridden count is the question a reviewer opens this screen
            to ask, which is why migration 0371 indexes it — a partial index on
            (company_id, ticket_id) WHERE amount_overridden, so the answer
            stays the size of the answer rather than the size of the table.

            Silent at zero. A ticket nobody has overridden is the ordinary case
            and a "0 overridden" chip on every screen would train the eye to
            skip the row it matters on. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-white">
            {ticketData?.ticketNumber || ticketData?.ticket_number || ticketId}
          </span>
          {overriddenTotal > 0 && (
            <span
              data-testid="overridden-count"
              className="rounded-fi-pill border border-fieldiq-accent/40 bg-fieldiq-accent/10 px-2 py-0.5 text-xs font-semibold text-fieldiq-accent-signal"
            >
              {overriddenTotal} amount{overriddenTotal === 1 ? '' : 's'} set by hand
            </span>
          )}
        </div>
        {/* Flash Message */}
        {message && (
          /* THE INK IS NEUTRAL AND THE GLYPH CARRIES THE HUE. A
             `--fi-signal-*` value is a FILL to put dark ink on, never an ink:
             as text on a theme-following surface it keeps its bright dark-mode
             value on a white panel. Measured on /payroll/approvals as rendered
             ink over the tint each is paired with — amber 1.49, red 2.08,
             green 1.52, against a floor of 4.5. */
          <div className={`flex items-center gap-2 rounded-fi-panel border px-4 py-3 text-sm font-medium text-fieldiq-text ${
            message.type === 'success'
              ? 'border-fieldiq-border bg-fieldiq-card'
              : 'border-fi-signal-red/40 bg-fieldiq-card'
          }`}>
            {message.type === 'success'
              ? <CheckCircle size={14} className="shrink-0 text-fieldiq-success" aria-hidden="true" />
              : <AlertCircle size={14} className="shrink-0 text-fieldiq-danger" aria-hidden="true" />}
            <span className="flex-1">{message.text}</span>
            {message.sticky && (
              <button
                onClick={() => setMessage(null)}
                aria-label="Dismiss"
                className="shrink-0 font-bold px-1 hover:opacity-70"
              >
                &times;
              </button>
            )}
          </div>
        )}

        {/* Ticket Summary Card */}
        {ticketData && (
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center gap-1 text-fieldiq-muted text-xs mb-0.5"><Users size={10} /> Customer</div>
                <p className="text-sm font-medium text-white">{ticketData.customer || ticketData.customer_name || '—'}</p>
              </div>
              <div>
                <div className="flex items-center gap-1 text-fieldiq-muted text-xs mb-0.5"><FileText size={10} /> Job</div>
                <p className="text-sm font-medium text-white">{ticketData.jobNumber || ticketData.job_number || '—'}</p>
              </div>
              <div>
                <div className="flex items-center gap-1 text-fieldiq-muted text-xs mb-0.5"><Calendar size={10} /> Date</div>
                <p className="text-sm font-medium text-white">{(() => { const d = ticketData.date || ticketData.work_date || ticketData.workDate; return formatCalendarDate(d ? String(d) : null, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US') ?? '—'; })()}</p>
              </div>
              <div>
                <div className="flex items-center gap-1 text-fieldiq-muted text-xs mb-0.5"><DollarSign size={10} /> Total</div>
                <p className="text-sm font-bold text-white">{fmtCurrency(ticketData.total ?? ticketData.subtotal)}</p>
              </div>
            </div>
          </div>
        )}

        {/* All Approved Banner with Unapprove option */}
        {allApproved && (
          <div className="flex items-center justify-between gap-4 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
            <div className="flex items-center gap-3">
              <Lock size={18} className="shrink-0 text-fieldiq-success" aria-hidden="true" />
              <div>
                <p className="flex items-center gap-2 text-sm font-bold text-white">
                  {/* The saturated badge idiom — a signal FILL with
                      `text-fi-signal-ink` on it, which holds in both themes.
                      The heading beside it stays theme ink. */}
                  <span className="rounded-fi-pill border border-black/20 bg-fi-signal-green px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.06em] text-fi-signal-ink">
                    Approved
                  </span>
                  All Assignments Approved
                </p>
                <p className="text-xs text-fieldiq-text">Pay assignments are locked and sent to payroll.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => unapproveMutation.mutate()}
              disabled={unapproveMutation.isPending}
              className="shrink-0 rounded-fi-pill border border-fieldiq-border bg-fieldiq-dark px-4 py-2 text-xs font-medium text-white transition-colors hover:border-fieldiq-accent disabled:cursor-not-allowed disabled:opacity-60"
            >
              {/* The label states what is happening rather than staying still
                  and greying out. A disabled button with unchanged text reads
                  as broken; this one reads as busy. */}
              {unapproveMutation.isPending ? 'Unlocking…' : 'Unlock & Edit'}
            </button>
          </div>
        )}

        {/* AI Reasoning Banner */}
        {aiResult && rollUpConfidence && (() => {
          const conf = confidenceBadge(rollUpConfidence);
          return (
            /* NO PURPLE. The signal palette has five hues — amber, blue,
               green, red, slate — and no purple, so a purple here is a hue
               with no light-mode or contrast review behind it that means
               something on this one screen. The panel is the ordinary card and
               the Sparkles glyph is what says "this came from the AI". */
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles size={16} className="shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
                <h3 className="text-sm font-bold text-white">AI Analysis</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 border ${conf.bg} ${conf.text} ${conf.border}`}>
                  <conf.Icon size={11} /> {rollUpConfidence} confidence
                </span>
              </div>
              <p className="text-sm text-fieldiq-text">{aiResult.reasoning}</p>
            </div>
          );
        })()}

        {/* Low/medium confidence review warning */}
        {!isLoading && needsReview.length > 0 && (
          <div className="flex items-start gap-3 rounded-fi-panel border border-fi-signal-red/40 bg-fieldiq-card p-4">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-fieldiq-danger" aria-hidden="true" />
            <div>
              <p className="text-sm font-bold text-white">
                {needsReview.length} of {assignments.length} row{assignments.length !== 1 ? 's' : ''} need review before approval
              </p>
              <p className="text-xs text-fieldiq-text mt-0.5">
                AI-suggested ({lowestConfidence(needsReview.map(a => a.ai_confidence), 'medium')} confidence) — please verify the
                highlighted rows below. These are unverified guesses and will feed payroll once approved.
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader size={24} className="animate-spin text-fieldiq-accent-signal" aria-hidden="true" />
          </div>
        )}

        {/* Empty State — now gated on there being no GROUPS, not no assignments.
            A ticket with line items and no assignments is no longer empty: it
            renders a row per line item, each one saying it pays nobody. This
            state is the genuinely empty one — nothing on the ticket to pay from. */}
        {!isLoading && lineItemGroups.length === 0 && (
          <div className="text-center py-16 space-y-4">
            <DollarSign size={40} className="mx-auto text-fieldiq-muted" />
            <div>
              <p className="text-white font-medium">Nothing to assign pay to</p>
              <p className="text-sm text-fieldiq-text mt-1">This ticket has no line items, so there is no revenue for payroll to pay out of. Add line items to the ticket first.</p>
            </div>
            {/* No "AI Suggest Pay" call to action here. pay-ai.service.ts returns
                zero suggestions and the reasoning "No line items on this ticket"
                when the ticket has none, so in this state the button is a control
                that provably cannot do anything. The header's AI Suggest button
                is still there for anyone who wants to prove that for themselves. */}
          </div>
        )}

        {/* AI prompt bar — full width, directly above the list, and the only AI
            Suggest control on screen while it is up. Its condition and the
            header button's are the same boolean read twice, so the two cannot
            disagree about which one the operator is looking at. */}
        {showAiPromptBar && (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
            <div className="flex min-w-0 items-start gap-2.5">
              <Sparkles size={16} className="mt-0.5 shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">No pay assigned on this ticket yet</p>
                <p className="text-xs text-fieldiq-text mt-0.5">
                  {anyLineNamesCrew
                    ? 'AI can suggest pay for each line from the crew named on it. A line naming nobody pays nobody — add a worker to it by hand.'
                    : 'No crew is named on any line, so AI has nothing to suggest pay from. Name a hand on the ticket, or add a worker to any line by hand.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => aiMutation.mutate()}
              disabled={aiMutation.isPending}
              className="fi-action fi-action--standard fi-action--primary flex shrink-0 items-center gap-1.5 disabled:opacity-50"
            >
              {aiMutation.isPending ? (
                <><Loader size={14} className="animate-spin" aria-hidden="true" /> Thinking...</>
              ) : (
                <><Sparkles size={14} aria-hidden="true" /> AI Suggest Pay</>
              )}
            </button>
          </div>
        )}

        {/* Line Item Groups */}
        {!isLoading && lineItemGroups.map((group) => (
          <LineItemCard
            key={group.lineItemId}
            group={group}
            expanded={expandedItems.has(group.lineItemId)}
            onToggle={() => toggleExpanded(group.lineItemId)}
            allAssignments={assignments}
            onUpdateAssignment={updateLocalAssignment}
            onRemoveAssignment={removeLocalAssignment}
            findingFor={findingFor}
            configuredRate={configuredRate}
            onAddWorker={addManualAssignment}
            crewMembers={crewMembers}
            deleteConfirm={deleteConfirm}
            setDeleteConfirm={setDeleteConfirm}
            allUsers={allUsers}
            overrideOpen={overrideOpen}
            onToggleOverride={toggleOverrideOpen}
            onSetOverride={setOverrideAmount}
            onClearOverride={clearOverride}
            onSetShifts={setShifts}
            refusalFor={(id?: string) => (id ? costingReport?.refusals[id] : undefined)}
            saveRefusalFor={(a: PayAssignment) => (
              a.ticket_line_item_id && a.user_id
                ? saveRefusals?.[refusalKey(a.ticket_line_item_id, a.user_id)]
                : undefined
            )}
            differenceFor={(id?: string) =>
              (id ? costingReport?.differences.find(d => d.assignmentId === id) : undefined)}
          />
        ))}

        {/* ── The total, and the one action the page is for ─────────────────
            Inside the content container now rather than pinned to the page
            edge, so it lines up with the panels above it, and on
            `--bottom-chrome` padding so the last row clears the mobile bar.

            THE FIGURE IS NOT GREEN. It carried 400-weight green ink, which is
            money coloured as though it were a status; the amount is already the
            emphasised thing by size and weight, and green here competes with
            the approved state that genuinely is one. */}
        {!isLoading && assignments.length > 0 && (
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-white">Total Pay Assigned</p>
                <p className="text-xs text-fieldiq-muted">{assignments.length} assignment{assignments.length !== 1 ? 's' : ''}{ticketData ? ` · Revenue: ${fmtCurrency(ticketData.total ?? ticketData.subtotal)}` : ''}</p>
              </div>
              <p className="text-xl font-bold text-white">{fmtCurrency(totalPay)}</p>
            </div>
            {!allApproved && (
              <div className="flex gap-3 border-t border-fieldiq-border px-4 py-3">
                {dirty && (
                  <button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                    className="fi-action fi-action--standard fi-action--primary flex flex-1 items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saveMutation.isPending ? (
                      <><Loader size={14} className="animate-spin" aria-hidden="true" /> Saving...</>
                    ) : (
                      <><Save size={14} aria-hidden="true" /> Save Assignments</>
                    )}
                  </button>
                )}
                {/* APPROVE ALL IS THE PRIMARY. It was the grey secondary and
                    read as disabled — missed in a live demo, so the pay never
                    reached payroll. Amber whenever there is something to
                    approve; the muted shape only while nothing can happen. */}
                {!dirty && (
                  <button
                    onClick={() => approveMutation.mutate()}
                    disabled={!canApproveAll}
                    className={`fi-action fi-action--standard flex flex-1 items-center justify-center gap-2 ${
                      canApproveAll
                        ? 'fi-action--primary'
                        : 'border border-fieldiq-border bg-fieldiq-dark text-white opacity-50'
                    }`}
                  >
                    {approveMutation.isPending ? (
                      <><Loader size={14} className="animate-spin" aria-hidden="true" /> Approving...</>
                    ) : (
                      <><CheckCircle size={14} aria-hidden="true" /> Approve All</>
                    )}
                  </button>
                )}
                {!dirty && shiftsBlocked > 0 && (
                  <p className="self-center text-xs text-fieldiq-text" role="status" data-testid="approve-blocked-shifts">
                    Enter shifts first ({shiftsBlocked} row{shiftsBlocked === 1 ? '' : 's'})
                  </p>
                )}
                {!dirty && rateBlocked > 0 && (
                  <p className="self-center text-xs text-fieldiq-text" role="status" data-testid="approve-blocked-rate">
                    Set a rate first ({rateBlocked} row{rateBlocked === 1 ? '' : 's'})
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </section>
    </div>
  );
}

// ==========================================
// LINE ITEM CARD
// ==========================================
function LineItemCard({
  group,
  expanded,
  onToggle,
  allAssignments,
  onUpdateAssignment,
  onRemoveAssignment,
  findingFor,
  configuredRate,
  onAddWorker,
  crewMembers,
  deleteConfirm,
  setDeleteConfirm,
  allUsers,
  overrideOpen,
  onToggleOverride,
  onSetOverride,
  onClearOverride,
  onSetShifts,
  refusalFor,
  saveRefusalFor,
  differenceFor,
}: {
  group: LineItemGroup;
  expanded: boolean;
  onToggle: () => void;
  allAssignments: PayAssignment[];
  onUpdateAssignment: (index: number, updates: Partial<PayAssignment>) => void;
  onRemoveAssignment: (index: number) => void;
  findingFor: (index: number) => TierFinding | undefined;
  configuredRate: (userId: string, lineItemType: string | undefined, payType: string) => ConfiguredRate;
  onAddWorker: (lineItemId: string, userId: string) => void;
  crewMembers: { id: string; firstName: string; lastName: string; initials: string }[];
  deleteConfirm: string | null;
  setDeleteConfirm: (id: string | null) => void;
  allUsers: { id: string; firstName?: string; lastName?: string; first_name?: string; last_name?: string }[];
  overrideOpen: Set<number>;
  onToggleOverride: (index: number) => void;
  onSetOverride: (index: number, amount: number | null) => void;
  onClearOverride: (index: number) => void;
  onSetShifts: (index: number, value: number | null) => void;
  refusalFor: (assignmentId?: string) => string | undefined;
  /** Why the last SAVE ATTEMPT refused this row. Keyed on the row, not on an id it does not have. */
  saveRefusalFor: (assignment: PayAssignment) => SaveRefusal | undefined;
  differenceFor: (assignmentId?: string) => ServerDifference | undefined;
}) {
  const groupTotal = sumMoney(group.assignments.map((a) => a.calculated_amount));

  // The office's typed shifts on this line against its quantity — a hint
  // while editing. The server checks the same sum on Save and refuses a
  // mismatch, so this never decides anything on its own.
  const shiftRows = takesShifts(group.shiftsRule)
    ? group.assignments.filter(a => a.pay_type === 'day_rate') : [];
  const anyShiftsEntered = shiftRows.some(a => parseShifts(a.shifts_worked) != null);
  const tally = shiftsTally(shiftRows.map(a => parseShifts(a.shifts_worked)), group.quantity);

  /**
   * THREE states, not two. #458 shipped with only two and conflated the middle
   * one: it asked "has anyone been paid yet", so on any ticket before AI Suggest
   * has run EVERY line read "Pays nobody" — including lines with a full crew in
   * their tech slots. Smith's FT-00055 showed all four lines that way while
   * three of them named TM, CH and WG/RD/BS. Only the CRT line named nobody.
   *
   *   noAssignments && no techs  -> pays nobody. Deliberate, amber.
   *   noAssignments && techs     -> not assigned YET. Neutral, and it says who.
   *   assignments                -> unchanged.
   */
  const noAssignments = group.assignments.length === 0;
  const namedTechs = group.techSlots;
  const paysNobody = noAssignments && namedTechs.length === 0;
  const awaitingAssignment = noAssignments && namedTechs.length > 0;

  // Prefer what the field wrote. A slot can carry an id with no initials once a
  // tenant's id columns are backfilled, so fall back to the crew list, and to
  // the slot number only if the id names nobody we know.
  const techLabels = namedTechs.map((t) => {
    if (t.initials) return t.initials;
    const crew = crewMembers.find((c) => c.id === t.id);
    return crew ? crew.initials : `Tech ${t.slot}`;
  });

  // Calculate total percentage allocation for this line item
  const percentAssignments = group.assignments.filter(
    a => isPercentagePayType(a.pay_type)
  );
  const totalPctAllocation = percentAssignments.reduce(
    (s, a) => s + (Number(a.percentage_applied) || 0), 0
  );
  const totalPctDisplay = Math.round(totalPctAllocation * 10000) / 100;
  const hasPercentages = percentAssignments.length > 0;
  /* ONE INK, and the over-100% case is called out by a BADGE beside it rather
     than by recolouring the figure. Three shades of hue on one number asked
     the reader to decode a colour scale nothing on screen explains, and in
     light mode all three were remapped to dark values that read almost the
     same anyway. The only allocation state worth a signal is the error, and it
     already has a saturated badge. */
  const allocationColor = 'text-fieldiq-text';
  const allocationBorder = totalPctDisplay > 100 ? 'border-fi-signal-red/50' : '';

  // Amber, not red: the accent already means "look at this" across the app,
  // where red is reserved for the over-100% allocation error just above.
  // Only state 1 earns it. A line whose crew is named and simply has no pay
  // assigned yet is the ordinary state of every fresh ticket — colouring that
  // amber is what made the whole screen shout on FT-00055.
  const cardBorder = allocationBorder || (paysNobody ? 'border-fieldiq-accent/40' : 'border-fieldiq-border');

  return (
    <div className={`bg-fieldiq-card rounded-xl border overflow-hidden ${cardBorder}`}>
      {/* Line Item Header */}
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between hover:bg-fieldiq-dark/30 transition-colors">
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-fieldiq-accent uppercase">{group.type}</span>
            {group.assignments.every(a => a.approved) && group.assignments.length > 0 && (
              <Lock size={12} className="text-fieldiq-success" aria-hidden="true" />
            )}
          </div>
          <p className="text-sm font-medium text-white mt-0.5">{group.description}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-fieldiq-text">
            <span>Revenue: {fmtCurrency(group.total)}</span>
            <span>Pay: {fmtCurrency(groupTotal)}</span>
            {paysNobody && (
              <span className="text-fieldiq-accent font-semibold">Pays nobody</span>
            )}
            {awaitingAssignment && (
              <span className="text-fieldiq-text">
                Pay not assigned yet · {techLabels.join(', ')}
              </span>
            )}
            {!noAssignments && (
              <span>{group.assignments.length} worker{group.assignments.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          {anyShiftsEntered && (
            <p
              className={`mt-1 text-xs ${tally.complete && tally.adds ? 'text-fieldiq-text' : 'text-fieldiq-text font-semibold'}`}
              data-testid="shifts-tally"
            >
              Shifts {fmtQty(tally.sum)} of {fmtQty(group.quantity)}
              {tally.complete && tally.adds === false && ' · must add up to the line qty'}
            </p>
          )}
          {/* Allocation summary */}
          {hasPercentages && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-xs font-semibold ${allocationColor}`}>
                {percentAssignments.map(a =>
                  fmtPayPercentage(a.percentage_applied, { maxDecimals: 2, absent: '—' })
                ).join(' + ')}
                {percentAssignments.length > 1 ? ` = ${totalPctDisplay}%` : ''}
                {' allocated'}
              </span>
              {totalPctDisplay > 100 && (
                <span className="flex items-center gap-1 rounded-fi-pill border border-black/20 bg-fi-signal-red px-1.5 py-0.5 text-xs font-semibold uppercase text-fi-signal-ink">
                  <AlertCircle size={10} aria-hidden="true" /> Over 100%
                </span>
              )}
            </div>
          )}
        </div>
        {expanded ? <ChevronUp size={16} className="text-fieldiq-muted" /> : <ChevronDown size={16} className="text-fieldiq-muted" />}
      </button>

      {/* Assignment Rows */}
      {expanded && (
        <div className="border-t border-fieldiq-border divide-y divide-fieldiq-border">
          {/* The deliberate state for a line nobody is paid from. It is not an
              error and it is not a gap — it says what payroll will do with this
              line, and the "+ Add worker" select below it is the same working
              control every other row has. */}
          {paysNobody && (
            <div className="px-4 py-3 flex items-start gap-2.5 bg-fieldiq-accent/[0.06]">
              <Users size={14} className="text-fieldiq-accent shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-white">This line pays nobody</p>
                <p className="text-xs text-fieldiq-text mt-0.5">
                  Nobody is named in its tech slots and no pay is assigned to it, so
                  payroll will skip it. Name someone below if that is wrong.
                </p>
              </div>
            </div>
          )}

          {/* State 2. No accent wash and no accent icon — this is the ordinary
              condition of a ticket nobody has run AI Suggest on, not something
              to act on. It says who worked the line so the office can see the
              names are already there. */}
          {awaitingAssignment && (
            <div className="px-4 py-3 flex items-start gap-2.5">
              <Users size={14} className="text-fieldiq-muted shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-white">Pay not assigned yet</p>
                <p className="text-xs text-fieldiq-text mt-0.5">
                  The field named{' '}
                  <span className="text-white font-medium">{techLabels.join(', ')}</span>
                  {' '}on this line. Run AI Suggest, or add a worker below, to turn that
                  into pay.
                </p>
              </div>
            </div>
          )}
          {group.assignments.map((assignment) => {
            const globalIndex = allAssignments.indexOf(assignment);
            const confirmKey = assignment.id || `local-${globalIndex}`;
            return (
              <AssignmentRow
                key={confirmKey}
                assignment={assignment}
                index={globalIndex}
                onUpdate={onUpdateAssignment}
                onRemove={onRemoveAssignment}
                finding={findingFor(globalIndex)}
                configuredRate={configuredRate(assignment.user_id, group.type, assignment.pay_type)}
                share={priceDayRate(group, assignment)}
                lineType={group.type}
                showDeleteConfirm={deleteConfirm === confirmKey}
                onDeleteConfirm={() => setDeleteConfirm(confirmKey)}
                onCancelDelete={() => setDeleteConfirm(null)}
                allUsers={allUsers}
                overrideRevealed={overrideOpen.has(globalIndex)}
                onToggleOverride={() => onToggleOverride(globalIndex)}
                onSetOverride={onSetOverride}
                onClearOverride={() => onClearOverride(globalIndex)}
                shiftsRule={group.shiftsRule}
                onSetShifts={onSetShifts}
                computeRefusal={refusalFor(assignment.id)}
                saveRefusal={saveRefusalFor(assignment)}
                difference={differenceFor(assignment.id)}
              />
            );
          })}
          {/* Add Worker */}
          <div className="px-4 py-3">
            <div className="flex items-center gap-2">
              <Plus size={14} className="text-fieldiq-accent" />
              <select
                className="flex-1 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white focus:border-fieldiq-accent focus:outline-none"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    onAddWorker(group.lineItemId, e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">+ Add worker...</option>
                {crewMembers
                  .filter(c => !group.assignments.some(a => a.user_id === c.id))
                  .map(c => (
                    <option key={c.id} value={c.id}>{c.initials} — {c.firstName} {c.lastName}</option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// ASSIGNMENT ROW
// ==========================================
function AssignmentRow({
  assignment,
  index,
  onUpdate,
  onRemove,
  finding,
  configuredRate,
  share,
  lineType,
  showDeleteConfirm,
  onDeleteConfirm,
  onCancelDelete,
  allUsers,
  overrideRevealed,
  onToggleOverride,
  onSetOverride,
  onClearOverride,
  shiftsRule,
  onSetShifts,
  computeRefusal,
  saveRefusal,
  difference,
}: {
  assignment: PayAssignment;
  index: number;
  onUpdate: (index: number, updates: Partial<PayAssignment>) => void;
  onRemove: (index: number) => void;
  finding?: TierFinding;
  configuredRate: ConfiguredRate;
  /** What the line prices this day_rate row at; null when the row is not on a day rate or has no rate. */
  share: FlatRateEarnings | null;
  lineType: string;
  showDeleteConfirm: boolean;
  onDeleteConfirm: () => void;
  onCancelDelete: () => void;
  allUsers: { id: string; firstName?: string; lastName?: string; first_name?: string; last_name?: string }[];
  /** Is the amount field revealed on this row? Not the same as overridden. */
  overrideRevealed: boolean;
  onToggleOverride: () => void;
  onSetOverride: (index: number, amount: number | null) => void;
  onClearOverride: () => void;
  /** Does this row's line take shifts (API's answer; null = not said). */
  shiftsRule: ShiftsRule | null;
  onSetShifts: (index: number, value: number | null) => void;
  /**
   * Why the server could not price this row, as the LAST SAVE reported it.
   * Session-scoped — no column carries it — so a reloaded row shows the state
   * without the reason rather than a reason that may no longer apply.
   */
  computeRefusal?: string;
  /**
   * Why the LAST SAVE ATTEMPT refused this row, in the server's own words.
   *
   * Distinct from `computeRefusal` above, which explains a row that WAS stored
   * with no computed figure because an override rescued it. This one is a row
   * that was not stored at all — and it is never set at the same time as that
   * one, because an overridden row rides past the batch refusal.
   */
  saveRefusal?: SaveRefusal;
  /** What the LAST SAVE stored in place of the figure that was on screen. */
  difference?: ServerDifference;
}) {
  const locked = assignment.approved;
  const override: OverrideState = overrideStateOf(assignment);
  const overrideAuthor = overrideAuthorName(
    override.kind === 'computed' ? null : override.by, allUsers,
  );
  const timeZone = useTenantTimeZone();
  const overrideWhen = formatOverrideAt(override.kind === 'computed' ? null : override.at, timeZone);
  const name = `${assignment.first_name || ''} ${assignment.last_name || ''}`.trim() || 'Unknown';
  const initials = assignment.initials || getInitials(assignment.first_name, assignment.last_name);
  const confidence = assignment.ai_confidence;
  const conf = confidence ? confidenceBadge(confidence) : null;
  const needsReview = confidence === 'low' || confidence === 'medium';

  // ── Day rate × shifts ─────────────────────────────────────────────────────
  // The shifts box shows on a day-rate row whose line takes shifts, OR on any
  // row the last save refused for shifts — the second is the door when the
  // payload carries no rule (a pre-follow-up API).
  const shiftsRefusedOnSave = saveRefusal?.reason === 'needs_shifts' || saveRefusal?.reason === 'shifts_mismatch';
  const showShifts = assignment.pay_type === 'day_rate' && (takesShifts(shiftsRule) || shiftsRefusedOnSave);
  const resolution: ShiftsResolution | null = assignment.shifts_stale
    ? null : shiftsResolutionOf(assignment.shifts_resolution);
  const ownShifts = parseShifts(assignment.shifts_worked);
  const needsShifts = showShifts && (
    shiftsRefusedOnSave
    || resolution?.kind === 'refused'
    || (shiftsRule === 'entry_required' && ownShifts == null)
  );
  // ── Equipment day line, no percentage (api#1184) ──────────────────────────
  // From AI Suggest's needsRate[], or from a save the server refused for it.
  // An override is the office's answer, so it clears the flag.
  const rateRefusedOnSave = saveRefusal?.reason === 'needs_rate';
  const needsRate = !locked && assignment.amount_overridden !== true
    && (assignment.needs_rate === true || rateRefusedOnSave);

  return (
    <div className={`px-4 py-3 ${locked ? 'opacity-75' : ''} ${confidence ? CONFIDENCE_ROW_STYLE[confidence] : ''}`}>
      <div className="flex items-center gap-3">
        {/* Avatar */}
        {/* AMBER INK ON ITS OWN 20% AMBER WASH MEASURED 3.13 IN LIGHT, against a
            floor of 4.5 — measured in a real browser, both themes, with
            transitions killed first.

            The swap CLAUDE.md prescribes for amber ink does NOT fix this one,
            and that is worth writing down because it is the first thing the
            next reader will try: theme.css already remaps the accent utility
            to the signal value under the light attribute, so both spellings
            render the same pixels and both measure 3.13. The wash is the
            problem, not the ink.

            So the wash goes and the surface becomes the page tone, which takes
            the same ink to 4.36 — still short. The initials therefore carry
            theme ink, which is what the name beside them already uses, and the
            amber survives as the ring. Measured 13.8 dark / 12.6 light. */}
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-fieldiq-accent/40 bg-fieldiq-dark text-xs font-bold text-white">
          {initials}
        </div>

        {/* Name & Role */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-white truncate">{name}</p>
            {locked && <Lock size={11} className="shrink-0 text-fieldiq-success" aria-hidden="true" />}
            {conf && (
              <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border flex items-center gap-1 shrink-0 ${conf.bg} ${conf.text} ${conf.border}`}>
                <conf.Icon size={10} /> {conf.label}
              </span>
            )}
          </div>
          {/* READ-ONLY, approved or not. The role is derived from the line item
              server-side, so an editable field here would be one the office can
              type into and watch do nothing — the inert-input failure this
              screen already carries elsewhere. A row added by hand has no label
              until Save, and renders as a dash rather than as a guess. */}
          <p className="text-xs text-fieldiq-text" data-testid="role-on-ticket">
            {assignment.role_on_ticket || '—'}
          </p>
        </div>

        {/* ── Amount ───────────────────────────────────────────────────────
            WHAT PAYS IS ALWAYS THE PRIMARY FIGURE, whoever decided it. The
            second number is the system's, and it appears only when there is
            one AND it disagrees — a row that agrees with itself gets no
            decoration at all, because "$800 / $800" is noise that teaches the
            eye to skip the rows where the two really do differ.

            A refused costing has no second number, and the words are "not
            computed". Not a blank, which reads as agreement, and above all not
            $0.00: a wrong number that looks right is worse than a gap, and a
            zero here is a hand's day of pay. */}
        <div className="text-right shrink-0">
          {/* NOT GREEN. An amount is not a status, and the green competed with
              the approved state that genuinely is one. Weight and size already
              make it the row's primary figure. */}
          <p className="text-sm font-bold text-white" data-testid="paid-amount">
            {fmtCurrency(assignment.calculated_amount)}
          </p>
          {override.kind === 'differs' && (
            <p className="text-xs text-fieldiq-muted" data-testid="computed-amount">
              computed {fmtCurrency(override.computed)}
            </p>
          )}
          {override.kind === 'uncomputed' && (
            <p className="text-xs text-fieldiq-muted" data-testid="computed-amount">
              not computed
            </p>
          )}
          <p className="text-xs text-fieldiq-muted">{fmtPayType(assignment.pay_type)}</p>
        </div>

        {/* Delete Button */}
        {!locked && (
          <button
            onClick={onDeleteConfirm}
            // An icon-only control with no accessible name. It was reachable in
            // a test only by being the single button in the row, which stopped
            // being true the moment the row gained the override control — and
            // was never true for a screen reader.
            aria-label={`Remove ${name} from this line`}
            className="shrink-0 rounded-lg p-1.5 text-fieldiq-muted transition-colors hover:bg-fieldiq-dark hover:text-fieldiq-danger"
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* AI reasoning — always visible for anything below high confidence */}
      {needsReview && assignment.ai_reasoning && conf && (
        /* The BADGE above carries the hue on its fill; this line is prose and
           takes theme ink. `conf.text` is `text-fi-signal-ink`, which is a
           near-black meant to sit ON a saturated fill — as ink on the card it
           would be almost invisible in dark mode. */
        <div className="ml-12 mt-2 flex items-start gap-1.5">
          <conf.Icon size={11} className={`${conf.ink} mt-0.5 shrink-0`} aria-hidden="true" />
          <p className={`text-xs ${conf.ink} leading-snug`}>
            <span className="font-semibold">Why: </span>
            {assignment.ai_reasoning}
          </p>
        </div>
      )}

      {/* ── The crew-size tier flag ───────────────────────────────────────────
          Rendered ONLY for `tier_drift`. Every other status is either nothing
          to say or something deliberately not said: `crew_ambiguous` is a line
          whose crew cannot be counted (two tech slots, one token — TNT's two
          Salyers), and `not_a_tier_rate` is a percentage matching no tier this
          worker has, which means somebody typed it.

          IT STATES WHAT WAS NOTICED AND OFFERS NO CORRECTION. There is no
          "should be" and no Apply button, because nothing in the data can tell
          a stale tier from a percentage the office meant. The row is left
          exactly as it is and the office decides. Amber, not red: this is a
          look-at-this, not an error, and it sits beside a number that may be
          perfectly correct. */}
      {finding?.status === 'tier_drift' && finding.appliedTier && (
        <div className="mt-2 ml-12 flex items-start gap-1.5">
          <AlertTriangle size={11} className="text-fieldiq-accent shrink-0 mt-0.5" />
          <p className="text-xs text-fieldiq-accent leading-snug">
            <span className="font-semibold">Crew changed: </span>
            this percentage is {name.split(' ')[0]}&rsquo;s{' '}
            <span className="font-semibold">{finding.appliedTier}</span> rate, for{' '}
            {TIER_HANDS[finding.appliedTier]} &mdash; and{' '}
            <span className="font-semibold">
              {finding.crewSize} {finding.crewSize === 1 ? 'hand is' : 'hands are'}
            </span>{' '}
            on this line. Nothing has been changed.
          </p>
        </div>
      )}

      {/* ── The flat-rate refusal ─────────────────────────────────────────────
          Rendered when the row is on a flat basis and holds no rate. The same
          shape as the blank-tier refusal on a newly added worker: the field is
          left EMPTY for the office to fill, nothing substitutes a figure, and
          the amount above reads as absent rather than $0.00.

          Red, not amber: unlike the crew-changed flag this is not "look at
          this", it is a row that cannot be priced and will save without an
          amount. It clears the moment the office types a rate — that is their
          decision, and the same rule as a typed percentage.

          A failed fetch is NOT a refusal. It says the rates could not be
          loaded, because a lookup that errored proves nothing about whether a
          rate exists, and reporting "no rate" there would send the office to
          fix a settings screen that may be correct. */}
      {!locked
        && (assignment.pay_type === 'day_rate' || assignment.pay_type === 'hourly')
        && assignment.rate_applied == null
        && (configuredRate.status === 'none' || configuredRate.status === 'error') && (
        <div className="mt-2 ml-12 flex items-start gap-1.5" role="alert">
          <AlertCircle size={11} className="mt-0.5 shrink-0 text-fieldiq-danger" aria-hidden="true" />
          <p className="text-xs text-fieldiq-text leading-snug">
            {configuredRate.status === 'none' ? (
              <>
                <span className="font-semibold">No {fmtPayType(assignment.pay_type).toLowerCase()} configured: </span>
                {name.split(' ')[0]} has no {assignment.pay_type === 'day_rate' ? 'day rate' : 'hourly rate'} for{' '}
                <span className="font-semibold">{assignment.line_item_type || 'this line item type'}</span>
                {' '}in Pay Rates. Nothing was filled in and this row has no amount. Type
                the rate here, or set it under Payroll &rsaquo; Pay Rates.
              </>
            ) : (
              <>
                <span className="font-semibold">Pay rates could not be loaded: </span>
                nothing was filled in for this row. Type the rate, or reload and try again.
              </>
            )}
          </p>
        </div>
      )}

      {/* ── The person-day share ──────────────────────────────────────────────
          Only on a day_rate row whose type bills person-days (subsistence),
          and only while the row is editable — an approved row is history.

          THE REFUSAL is the line naming nobody. The share is quantity ÷ the
          hands named on the line, and a line that names nobody has not said
          how many hand-days it covers; dividing by an invented 1 pays one
          hand for every day on the line on the office's say-so alone. Red,
          like the no-rate refusal above it: the row cannot be priced and will
          save without an amount. FT-04419's subsistence line is this case.

          THE BREAKDOWN shows the arithmetic on every priced row, because the
          amount is not the rate and the office has to be able to see why.
          Amber when the days do not come out whole — a hand who joined
          partway through is ordinary, but the money moves, so it is said
          rather than rounded — and amber when the row holds a figure other
          than the share, which is what every subsistence row saved before
          this arithmetic existed looks like. */}
      {/* ── Day rate × shifts ───────────────────────────────────────────────
          The arithmetic is the SERVER's: its shifts, the row's rate, and the
          amount it stored. Nothing here multiplies — a stale row (shifts
          edited since the server answered) shows no line rather than a sum
          the server has not agreed to. */}
      {showShifts && resolution?.kind === 'shifts' && !needsShifts && (
        <p className="mt-1.5 ml-12 text-xs text-fieldiq-muted" data-testid="day-rate-shifts">
          {fmtCurrency(assignment.rate_applied)} × {fmtShifts(resolution.shifts)} ={' '}
          <span className="text-white font-medium">{fmtCurrency(assignment.calculated_amount)}</span>
        </p>
      )}
      {showShifts && assignment.shifts_stale && !needsShifts && (
        <p className="mt-1.5 ml-12 text-xs text-fieldiq-muted" data-testid="shifts-priced-on-save">
          Priced on save
        </p>
      )}
      {!locked && needsShifts && (
        <div className="mt-2 ml-12 flex flex-wrap items-center gap-2" role="alert" data-testid="needs-shifts">
          <span className="rounded-fi-pill border border-black/20 bg-fi-signal-amber px-1.5 py-0.5 text-xs font-semibold uppercase text-fi-signal-ink">
            Needs shifts
          </span>
          <span className="text-xs text-fieldiq-text">
            {(saveRefusal?.reason ?? (resolution?.kind === 'refused' ? resolution.reason : null)) === 'shifts_mismatch'
              ? 'Shifts must add up to the line qty'
              : 'Enter shifts per hand'}
          </span>
        </div>
      )}

      {needsRate && (
        <div className="mt-2 ml-12 flex flex-wrap items-center gap-2" role="alert" data-testid="needs-rate">
          <span className="rounded-fi-pill border border-black/20 bg-fi-signal-amber px-1.5 py-0.5 text-xs font-semibold uppercase text-fi-signal-ink">
            Needs rate
          </span>
          <span className="text-xs text-fieldiq-text">
            No % set for this type — set it in pay rates or enter an amount
          </span>
        </div>
      )}

      {!locked && share?.status === 'no_crew' && (
        <div className="mt-2 ml-12 flex items-start gap-1.5" role="alert">
          <AlertCircle size={11} className="mt-0.5 shrink-0 text-fieldiq-danger" aria-hidden="true" />
          <p className="text-xs text-fieldiq-text leading-snug">
            <span className="font-semibold">Nobody is named on this line: </span>
            {getLineItemTypeLabel(lineType)} is paid per hand per day. This line bills{' '}
            <span className="font-semibold">{fmtQty(share.personDays)} person-day{share.personDays === 1 ? '' : 's'}</span>
            {' '}but names nobody in its tech slots, so {name.split(' ')[0]}&rsquo;s share cannot be
            worked out. Nothing was filled in and this row has no amount. Name the hands on
            the line, then set the rate again.
          </p>
        </div>
      )}
      {!locked && share?.status === 'priced' && share.usesPersonDays && (
        <div className="mt-1.5 ml-12 space-y-1">
          <p className="text-xs text-fieldiq-muted" data-testid="person-day-share">
            {fmtCurrency(assignment.rate_applied)} × {fmtQty(share.daysEach)} day{share.daysEach === 1 ? '' : 's'}
            {' '}({fmtQty(share.personDays)} person-day{share.personDays === 1 ? '' : 's'} ÷ {share.crew} named on the line)
            {' '}= <span className="text-white font-medium">{fmtCurrency(share.earnings)}</span>
          </p>
          {!share.isWholeDays && (
            <p className="text-xs text-fieldiq-accent flex items-start gap-1.5" role="status">
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>
                {fmtQty(share.personDays)} person-days across {share.crew} named on the line does not
                divide evenly ({fmtQty(share.daysEach)} days each). Paid exactly, not rounded &mdash; confirm the split.
              </span>
            </p>
          )}
          {assignment.calculated_amount != null
            && Math.abs(Number(assignment.calculated_amount) - share.earnings) >= 0.005 && (
            <p className="text-xs text-fieldiq-accent flex items-start gap-1.5" role="status">
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>
                This row holds <span className="font-semibold">{fmtCurrency(assignment.calculated_amount)}</span>, not
                the share. Set the rate again to reprice it, or leave it if the figure is deliberate.
              </span>
            </p>
          )}
        </div>
      )}

      {/* ── What the server stored instead ────────────────────────────────────
          The replacement, said on the row it happened to, in both currencies
          the office might recognise it by: the amount and the basis. The flash
          message carries the count; a count alone cannot be acted on, because
          it does not say WHICH rows or by how much.

          Amber, because this is the "look at this" case exactly — nothing
          failed, the save succeeded, and the number the office was looking at
          is not the number on file. That is the whole class of bug this screen
          was silent about.

          Only ever populated by the last save in this session. A reload clears
          it, which is honest: the row then simply holds what it holds. */}
      {/* ── The row the save refused ──────────────────────────────────────────
          RED, not amber, and that is the distinction with the block below it.
          Amber is "the save succeeded and replaced your figure — look at this".
          This is "the save did not happen", and on this route it did not happen
          for the whole ticket: the API returns before the insert, so every other
          row on screen is unsaved too.

          The server's own message, verbatim. It already names the worker, the
          line item type and what is missing; restating it in our words would be
          a second vocabulary for the same refusal, and the one the office reads
          would be the one that goes stale.

          The remedy is NOT here. It is in the banner, once, keyed on the reason
          — because "override this" is the right answer for a line whose crew
          cannot be counted and the wrong answer for a worker on the wrong basis,
          and a per-row sentence gets copied between the two. */}
      {/* A shifts refusal is said by the Needs shifts flag above, in four words;
          the server's sentence would repeat it at length. */}
      {saveRefusal && !shiftsRefusedOnSave && !(rateRefusedOnSave && needsRate) && (
        <div className="mt-2 ml-12 flex items-start gap-1.5" role="status" data-testid="save-refusal">
          <AlertCircle size={11} className="mt-0.5 shrink-0 text-fieldiq-danger" aria-hidden="true" />
          <p className="text-xs text-fieldiq-text leading-snug">
            <span className="font-semibold">Not saved: </span>
            {saveRefusal.message}
          </p>
        </div>
      )}

      {difference && !(showShifts && difference.client.calculatedAmount == null) && (
        <div className="mt-2 ml-12 flex items-start gap-1.5" role="status" data-testid="server-replaced">
          <AlertTriangle size={11} className="text-fieldiq-accent shrink-0 mt-0.5" />
          <p className="text-xs text-fieldiq-accent leading-snug">
            <span className="font-semibold">Priced by the server: </span>
            {difference.client.calculatedAmount != null
              && Math.abs(difference.client.calculatedAmount - difference.server.calculatedAmount) >= 0.005 && (
              <>
                this row was saved at <span className="font-semibold">{fmtCurrency(difference.server.calculatedAmount)}</span>,
                not the {fmtCurrency(difference.client.calculatedAmount)} on screen
                {difference.server.crewSize != null && <> ({difference.server.crewSize} on the line)</>}.{' '}
              </>
            )}
            {difference.client.payType && difference.client.payType !== difference.server.payType && (
              <>
                The basis was stored as <span className="font-semibold">{fmtPayType(difference.server.payType)}</span>,
                not {fmtPayType(difference.client.payType)}.{' '}
              </>
            )}
            To make a figure of your own stand instead, override the amount.
          </p>
        </div>
      )}

      {/* ── The decision line ─────────────────────────────────────────────────
          An overridden amount is a DECISION SOMEBODY MADE, and this is the line
          that says so. Without a name and a time beside it the row reads as an
          anomaly — a figure that disagrees with the system and nobody to ask
          about it — which is how a deliberate correction gets "fixed" back by
          the next person through.

          Neutral, not amber and not red. The accent means "look at this" and
          red is the over-allocation error; an override is neither. It is
          ordinary, recorded, and reversible.

          Shown on approved rows too. An approved override is exactly the one a
          reviewer needs attributed, and it is the one that can no longer be
          changed.

          The author may not resolve to a name — a deactivated colleague, a user
          list that has not loaded — and the id is NOT printed in its place. A
          raw UUID on screen is how a display gap gets reported as a data bug. */}
      {override.kind !== 'computed' && (
        <div className="mt-2 ml-12 flex items-start gap-1.5" data-testid="override-decision">
          <DollarSign size={11} className="text-fieldiq-text shrink-0 mt-0.5" />
          <div className="text-xs text-fieldiq-text leading-snug">
            <p>
              <span className="font-semibold text-white">Amount set by hand</span>
              {overrideAuthor ? <> by <span className="font-semibold text-white">{overrideAuthor}</span></> : null}
              {overrideWhen ? <> · {overrideWhen}</> : null}
              {override.kind === 'differs' && (
                <> · the system computed <span className="font-semibold text-white">{fmtCurrency(override.computed)}</span></>
              )}
            </p>
            {/* THE REFUSAL, in the server's own words. An override is allowed to
                rescue a row the costing could not price, so "not computed" here
                is a legitimate state and not a failure — but it is only half an
                answer without the reason, and the reason is the response's. */}
            {override.kind === 'uncomputed' && (
              <p className="mt-0.5">
                The system did not compute an amount for this row
                {computeRefusal ? <>: <span className="text-fieldiq-accent">{computeRefusal}</span></> : <> — no figure is on file to compare against.</>}
              </p>
            )}
            {/* CLEARING HAS TO BE VISIBLE OR THE OVERRIDE IS ONE-WAY. Nothing
                else on this screen can take the flag off: the recompute
                deliberately leaves an overridden row alone, so without this
                control the only way back to the system's figure is deleting the
                row and adding it again. */}
            {!locked && (
              <button
                onClick={onClearOverride}
                data-testid="clear-override"
                className="mt-1 text-xs font-semibold text-fieldiq-accent hover:underline"
              >
                Clear override{override.kind === 'differs' || override.kind === 'agrees'
                  ? ` — pay the computed ${fmtCurrency(override.computed)}`
                  : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Editable fields (not locked) */}
      {!locked && (
        <div className="mt-2 ml-12 flex items-center gap-2 flex-wrap">
          {/* Pay Type */}
          <select
            value={assignment.pay_type}
            onChange={(e) => onUpdate(index, { pay_type: e.target.value })}
            className="text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white outline-none"
          >
            {ASSIGNMENT_PAY_TYPES.map(v => <option key={v} value={v}>{PAY_TYPE_LABELS[v]}</option>)}
          </select>

          {/* Percentage Input */}
          {isPercentagePayType(assignment.pay_type) && (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={assignment.percentage_applied != null ? Math.round(assignment.percentage_applied * 10000) / 100 : ''}
                onChange={(e) => {
                  const pct = parseFloat(e.target.value) / 100;
                  onUpdate(index, { percentage_applied: isNaN(pct) ? null : pct });
                }}
                placeholder="25"
                className="w-16 text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white outline-none text-center"
              />
              <span className="text-xs text-fieldiq-muted">%</span>
              {assignment.percentage_applied != null && (
                <span className="text-xs text-fieldiq-muted ml-1">
                  = {fmtCurrency((assignment.base_amount ?? assignment.line_item_total ?? 0) * assignment.percentage_applied)}
                </span>
              )}
            </div>
          )}

          {/* Shifts Input — per hand, per-day labour lines only */}
          {showShifts && (
            <label className="flex items-center gap-1">
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.5"
                data-testid="shifts-input"
                aria-label={`Shifts for ${name}`}
                value={assignment.shifts_worked == null ? '' : String(Number(assignment.shifts_worked))}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  onSetShifts(index, Number.isFinite(v) && v > 0 ? v : null);
                }}
                placeholder={resolution?.kind === 'shifts' ? String(resolution.shifts) : '—'}
                className={`w-16 text-xs bg-fieldiq-dark border rounded-lg px-2 py-1.5 text-white outline-none text-center ${
                  needsShifts ? 'border-fi-signal-amber' : 'border-fieldiq-border'
                }`}
              />
              <span className="text-xs text-fieldiq-muted">shifts</span>
            </label>
          )}

          {/* Rate Input */}
          {(assignment.pay_type === 'day_rate' || assignment.pay_type === 'hourly') && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-fieldiq-muted">$</span>
              <input
                type="number"
                value={assignment.rate_applied ?? ''}
                onChange={(e) => {
                  const rate = parseFloat(e.target.value);
                  onUpdate(index, { rate_applied: isNaN(rate) ? null : rate });
                }}
                placeholder="0.00"
                className="w-20 text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white outline-none text-center"
              />
              {assignment.pay_type === 'hourly' && <span className="text-xs text-fieldiq-muted">/hr</span>}
            </div>
          )}

          {/* ── Overriding is deliberate ───────────────────────────────────
              A control that REVEALS the amount field, never a box sitting open
              on every row. The distinction is the whole point: an always-editable
              amount is an invitation, and what lands in it is indistinguishable
              afterwards from a figure the system worked out. Two clicks and a
              recorded author is what makes it a decision instead of a typo.

              The rate and percentage boxes to the left of this are the office's
              PROPOSAL — the server prices the row from the worker's rate card
              and reports any figure it replaced. This is the one control that
              makes a number of the office's own stand. */}
          {override.kind === 'computed' && !overrideRevealed && (
            <button
              onClick={onToggleOverride}
              data-testid="reveal-override"
              className="text-xs font-medium text-fieldiq-muted hover:text-fieldiq-accent underline underline-offset-2"
            >
              Override amount
            </button>
          )}
          {(overrideRevealed || override.kind !== 'computed') && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-fieldiq-accent font-semibold">Pay</span>
              <span className="text-xs text-fieldiq-muted">$</span>
              <input
                type="number"
                data-testid="override-amount"
                aria-label="Override amount"
                value={override.kind === 'computed' ? '' : (assignment.calculated_amount ?? '')}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  onSetOverride(index, Number.isNaN(v) ? null : v);
                }}
                placeholder="0.00"
                className="w-24 text-xs bg-fieldiq-dark border border-fieldiq-accent/50 rounded-lg px-2 py-1.5 text-white outline-none text-center"
              />
              {override.kind === 'computed' && (
                <button
                  onClick={onToggleOverride}
                  className="text-xs text-fieldiq-muted hover:text-fieldiq-accent-signal ml-1"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="mt-2 ml-12 flex items-center gap-2">
          <span className="text-xs text-fieldiq-text">Remove this assignment?</span>
          <button
            onClick={() => { onRemove(index); onCancelDelete(); }}
            className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-3 py-1 text-xs font-semibold text-fi-signal-ink transition-colors hover:opacity-90"
          >
            Remove
          </button>
          <button
            onClick={onCancelDelete}
            className="text-xs text-fieldiq-text hover:text-fieldiq-accent-signal transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
